import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import Checkbox from "@/components/ui/Checkbox.jsx";
import Button from "@/components/ui/Button.jsx";
import MessageComposerField, { clearRichMessageDraft } from "@/components/messaging/MessageComposerField.jsx";
import { composerDraftKey } from "@/components/messaging/messageDraftStore.mjs";
import SafeMessageContent from "@/components/messaging/SafeMessageContent.jsx";
import { useMessageTimelineScroll } from "@/components/messaging/useMessageTimelineScroll.js";
import FolaCardRenderer from "@/pages/messages/fola/components/FolaCardRenderer.jsx";
import FolaBookingAction from "@/pages/messages/fola/components/FolaBookingAction.jsx";
import FolaFormattedText from "@/pages/messages/fola/components/FolaFormattedText.jsx";
import {
  getActiveFolaPrompt,
  getFolaUiCopy,
  getInitialFolaItems,
  resolveFolaFreeformInput,
  resolveFolaSelection,
} from "@/pages/messages/fola/folaConfig.js";
import {
  buildFulfillmentFolaReply,
  classifyFulfillmentMessageIntent,
  fetchFulfillmentReplySnapshot,
  isFulfillmentConversationDescription,
  parseFulfillmentPrescriptionId,
} from "@/pages/messages/fola/fulfillmentReply.js";
import messagingService from "@/services/messagingService.js";
import { streamFolaOrchestratorTurn } from "@/services/folaOrchestratorClient.js";
import supabase from "@/services/supabaseClient.js";
import { loadLatestVitalRepPatientContext } from "@/services/vitalRepPatientContext.js";
import {
  formatMessagingExplicitTitle,
  resolveMessagingParticipantName,
} from "@/utils/messagingIdentity.js";
import {
  clearLastMessageThreadPath,
  writeLastMessageThreadPath,
} from "@/utils/messagesNavigationState.js";
import {
  getAccessibilityFolaStorageKey,
  readAccessibilityFolaPrefs,
} from "@/settings/accessibility.js";
import { getPharmacyFolaPrompt } from "@/pages/pharmacy/pharmacyFolaPrompts.js";
import { messageEntryRoute, messageEntryState, sendValidationError } from "@/pages/messages/messageEntryRouting.mjs";

const ICON_BACK = "/icons/doctor/arrow_back.svg";
const ICON_WRITE = "/icons/appointments/Chat with Fola/write.svg";
const ICON_ATTACH = "/icons/appointments/Chat with Fola/attach_file.svg";
const ICON_SEND = "/icons/appointments/Chat with Fola/arrow_upward.svg";
const ICON_DONE = "/icons/appointments/Chat with Fola/done.svg";
const ICON_DONE_ALL = "/icons/appointments/Chat with Fola/done_all.svg";
const ICON_FOLA = "/assets/images/fola.svg";
const FOLA_ICON_SIZE = "h-5 w-5";
const ICON_CHEVRON = "/icons/appointments/Chat with Fola/Show_less.svg";
const ICON_STOP = "/icons/appointments/Chat with Fola/Stop.svg";
const ICON_DELETE_BACKGROUND_CRITICAL = "/icons/doctor/DeleteBackgroundCritical.svg";
const TOAST_DURATION_MS = 3600;
const ACTIVE_MESSAGE_THREAD_EVENT = "afrimed:messages:thread-active";
const conversationUiStateCache = new Map();
const conversationSnapshotCache = new Map();

function isUuid(value) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    String(value || "").trim()
  );
}

function formatClock(value, locale = undefined, nowLabel = "Now") {
  if (!value) return nowLabel;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleTimeString(locale, { hour: "numeric", minute: "2-digit" });
}

function formatDayLabel(value, locale = undefined) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(locale, { month: "long", day: "numeric" });
}

function formatFolaBookingDate(value) {
  const iso = String(value || "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return "that date";
  return new Intl.DateTimeFormat("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${iso}T12:00:00.000Z`));
}

function formatConversationTitle(
  conversationId,
  state,
  { fallbackTitle = "Conversation", folaTitle = "Fola" } = {},
) {
  const explicit = String(
    state?.conversationTitle ||
      state?.participantName ||
      state?.patientName ||
      state?.doctorName ||
      ""
  ).trim();
  if (explicit) {
    return formatMessagingExplicitTitle(explicit, {
      isDoctor:
        Boolean(state?.doctorId) ||
        Boolean(state?.doctorName) ||
        String(state?.conversationType || "").trim().toLowerCase() === "doctor",
    });
  }
  if (conversationId === "fola") return folaTitle;
  if (isUuid(conversationId)) return fallbackTitle;
  const cleaned = String(conversationId || "").replace(/[-_]+/g, " ").trim();
  if (!cleaned) return fallbackTitle;
  return cleaned.replace(/\b\w/g, (match) => match.toUpperCase());
}

async function isValidPatientFulfillmentConversation({
  conversation,
  currentUserId,
}) {
  const cleanUserId = String(currentUserId || "").trim();
  const description = String(conversation?.description || "").trim();
  if (!cleanUserId || !isFulfillmentConversationDescription(description)) return true;

  const prescriptionId = parseFulfillmentPrescriptionId(description);
  if (!prescriptionId) return false;

  const { data: prescription, error: prescriptionError } = await supabase
    .from("prescriptions")
    .select("id,appointment_id,patient_user_id")
    .eq("id", prescriptionId)
    .maybeSingle();
  if (prescriptionError) throw prescriptionError;
  if (
    !prescription?.id ||
    String(prescription?.patient_user_id || "").trim() !== cleanUserId
  ) {
    return false;
  }

  const appointmentId = String(prescription?.appointment_id || "").trim();
  if (!appointmentId) return false;

  const { data: appointment, error: appointmentError } = await supabase
    .from("appointments")
    .select("id,patient_id")
    .eq("id", appointmentId)
    .maybeSingle();
  if (appointmentError) throw appointmentError;
  if (!appointment?.id) return false;

  const patientId = String(appointment?.patient_id || "").trim();
  if (!patientId) return false;

  const { data: patientRow, error: patientError } = await supabase
    .from("patients")
    .select("id,user_id")
    .eq("id", patientId)
    .maybeSingle();
  if (patientError) throw patientError;

  return String(patientRow?.user_id || "").trim() === cleanUserId;
}

function getFolaStorageKey(currentUserId) {
  const scopedUserId = String(currentUserId || "guest").trim() || "guest";
  return `afrimed:fola-thread:${scopedUserId}`;
}

function readStoredFolaItems(currentUserId) {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage?.getItem(getFolaStorageKey(currentUserId));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeStoredFolaItems(currentUserId, items) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage?.setItem(
      getFolaStorageKey(currentUserId),
      JSON.stringify(Array.isArray(items) ? items : []),
    );
  } catch {}
}

function clearStoredFolaItems(currentUserId) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage?.removeItem(getFolaStorageKey(currentUserId));
  } catch {}
}

function getHiddenMessagesStorageKey(currentUserId, conversationId) {
  const scopedUserId = String(currentUserId || "guest").trim() || "guest";
  const scopedConversationId = String(conversationId || "thread").trim() || "thread";
  return `afrimed:hidden-messages:${scopedUserId}:${scopedConversationId}`;
}

function readHiddenMessageIds(currentUserId, conversationId) {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage?.getItem(
      getHiddenMessagesStorageKey(currentUserId, conversationId)
    );
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? Array.from(
          new Set(
            parsed
              .map((value) => String(value || "").trim())
              .filter(Boolean)
          )
        )
      : [];
  } catch {
    return [];
  }
}

function writeHiddenMessageIds(currentUserId, conversationId, messageIds) {
  if (typeof window === "undefined") return;
  try {
    const normalized = Array.from(
      new Set(
        (Array.isArray(messageIds) ? messageIds : [])
          .map((value) => String(value || "").trim())
          .filter(Boolean)
      )
    );
    window.localStorage?.setItem(
      getHiddenMessagesStorageKey(currentUserId, conversationId),
      JSON.stringify(normalized)
    );
  } catch {}
}

function getConversationUiStateStorageKey(currentUserId, conversationId) {
  const scopedUserId = String(currentUserId || "guest").trim() || "guest";
  const scopedConversationId = String(conversationId || "thread").trim() || "thread";
  return `afrimed:conversation-ui:${scopedUserId}:${scopedConversationId}`;
}

function normalizeDeleteTargetMessageId(value) {
  return String(value || "").trim();
}

function normalizeConversationUiState(state) {
  const nextState =
    state && typeof state === "object" && !Array.isArray(state) ? state : {};

  return {
    draft: String(nextState.draft || ""),
    selectedOptions: Array.isArray(nextState.selectedOptions)
      ? nextState.selectedOptions
          .map((value) => String(value || "").trim())
          .filter(Boolean)
      : [],
    deleteTargetMessageId: normalizeDeleteTargetMessageId(nextState.deleteTargetMessageId),
    pendingAttachment: nextState.pendingAttachment || null,
  };
}

function readConversationUiState(currentUserId, conversationId) {
  const fallback = normalizeConversationUiState(null);
  if (typeof window === "undefined") return fallback;

  const storageKey = getConversationUiStateStorageKey(currentUserId, conversationId);
  const cachedState = normalizeConversationUiState(conversationUiStateCache.get(storageKey));

  try {
    const raw = window.sessionStorage?.getItem(storageKey);
    if (!raw) return cachedState;

    const parsed = normalizeConversationUiState(JSON.parse(raw));
    return {
      ...parsed,
      pendingAttachment: cachedState.pendingAttachment,
    };
  } catch {
    return cachedState;
  }
}

function writeConversationUiState(currentUserId, conversationId, state) {
  if (typeof window === "undefined") return;

  const storageKey = getConversationUiStateStorageKey(currentUserId, conversationId);
  const normalized = normalizeConversationUiState(state);
  conversationUiStateCache.set(storageKey, normalized);

  try {
    window.sessionStorage?.setItem(
      storageKey,
      JSON.stringify({
        draft: normalized.draft,
        selectedOptions: normalized.selectedOptions,
        deleteTargetMessageId: normalized.deleteTargetMessageId,
      })
    );
  } catch {}
}

function clearConversationUiState(currentUserId, conversationId) {
  if (typeof window === "undefined") return;

  const storageKey = getConversationUiStateStorageKey(currentUserId, conversationId);
  conversationUiStateCache.delete(storageKey);

  try {
    window.sessionStorage?.removeItem(storageKey);
  } catch {}
}

function releaseAttachmentPreview(attachment) {
  if (attachment?.previewUrl?.startsWith("blob:")) {
    URL.revokeObjectURL(attachment.previewUrl);
  }
}

function getConversationSnapshotStorageKey(currentUserId, conversationId) {
  const scopedUserId = String(currentUserId || "guest").trim() || "guest";
  const scopedConversationId = String(conversationId || "thread").trim() || "thread";
  return `afrimed:conversation-snapshot:${scopedUserId}:${scopedConversationId}`;
}

function readConversationSnapshot(currentUserId, conversationId) {
  const storageKey = getConversationSnapshotStorageKey(currentUserId, conversationId);
  return conversationSnapshotCache.get(storageKey) || null;
}

function writeConversationSnapshot(currentUserId, conversationId, snapshot) {
  const storageKey = getConversationSnapshotStorageKey(currentUserId, conversationId);
  conversationSnapshotCache.set(storageKey, snapshot);
}

function clearConversationSnapshot(currentUserId, conversationId) {
  const storageKey = getConversationSnapshotStorageKey(currentUserId, conversationId);
  conversationSnapshotCache.delete(storageKey);
}

function resolveLiveTitle(conversation, currentUserId, fallbackTitle) {
  const participants = Array.isArray(conversation?.conversation_participants)
    ? conversation.conversation_participants
    : [];
  const otherParticipant = participants.find(
    (participant) => participant?.user_id !== currentUserId
  );
  return (
    resolveMessagingParticipantName(otherParticipant, "") ||
    fallbackTitle ||
    conversation?.title ||
    "Conversation"
  );
}

function resolveOutgoingReadState(message, participants, currentUserId) {
  if (!message || message?.sender_id !== currentUserId) return null;

  const createdAt = new Date(message?.created_at || "");
  if (Number.isNaN(createdAt.getTime())) return "sent";

  const otherParticipants = (Array.isArray(participants) ? participants : []).filter(
    (participant) => participant?.user_id && participant?.user_id !== currentUserId
  );

  const isReadByOtherParticipant = otherParticipants.some((participant) => {
    const lastReadAt = new Date(participant?.last_read_at || "");
    return !Number.isNaN(lastReadAt.getTime()) && lastReadAt >= createdAt;
  });

  return isReadByOtherParticipant ? "read" : "sent";
}

async function mapLiveMessages(
  messages,
  currentUserId,
  participants = [],
  hiddenMessageIds = new Set(),
  {
    locale = undefined,
    nowLabel = "Now",
    deletedForMeText = "You deleted this message",
    deletedBySenderText = "Sender deleted message",
  } = {}
) {
  const items = [];
  let activeDayKey = "";
  const quotePrescriptionIds = Array.from(new Set(
    (Array.isArray(messages) ? messages : [])
      .map((message) => String(message?.metadata?.prescription_id || "").trim())
      .filter(Boolean),
  ));
  const { data: fulfillmentRows } = quotePrescriptionIds.length
    ? await supabase
        .from("prescription_fulfillments")
        .select("prescription_id,status")
        .in("prescription_id", quotePrescriptionIds)
    : { data: [] };
  const fulfilledStatusByPrescription = new Map(
    (Array.isArray(fulfillmentRows) ? fulfillmentRows : []).map((row) => [
      String(row?.prescription_id || "").trim(),
      String(row?.status || "").trim().toLowerCase(),
    ]),
  );

  for (const message of Array.isArray(messages) ? messages : []) {
    const messageId = String(message?.id || "").trim();
    if (messageId && hiddenMessageIds.has(messageId)) continue;

    const createdAt = String(message?.created_at || "").trim();
    const nextDayKey = createdAt ? createdAt.slice(0, 10) : "";
    const side = message?.sender_id === currentUserId ? "right" : "left";
    const metadata =
      message?.metadata && typeof message.metadata === "object" && !Array.isArray(message.metadata)
        ? message.metadata
        : {};
    const wasDeletedForEveryone =
      message?.message_type === "deleted" ||
      metadata?.deleted_for_everyone === true ||
      metadata?.deletedForEveryone === true;
    const time = formatClock(message?.created_at, locale, nowLabel);
    const canDelete = side === "right" && !wasDeletedForEveryone;

    const ensureDayLabel = () => {
      if (nextDayKey && nextDayKey !== activeDayKey) {
        activeDayKey = nextDayKey;
        items.push({
          id: `day-${nextDayKey}`,
          type: "date",
          label: formatDayLabel(createdAt, locale),
        });
      }
    };

    if (wasDeletedForEveryone) {
      ensureDayLabel();
      items.push({
        id: `${message.id}-deleted`,
        messageId: message.id,
        type: "deleted",
        side,
        text: side === "right" ? deletedForMeText : deletedBySenderText,
        time,
        canDelete: false,
      });
      continue;
    }

    const attachments = Array.isArray(message?.message_attachments)
      ? message.message_attachments
      : [];
    const imageAttachments = [];

    for (const attachment of attachments) {
      const url = await messagingService.getAttachmentUrl(attachment?.file_path);
      if (url && attachment?.file_type === "image") {
        imageAttachments.push(url);
      }
    }

    const content = String(message?.content || "").trim();
    const readState = resolveOutgoingReadState(message, participants, currentUserId);
    const isAttachmentPlaceholder =
      imageAttachments.length > 0 && /^shared an image$/i.test(content);

    if (imageAttachments.length > 0) {
      ensureDayLabel();
      items.push({
        id: `${message.id}-rich`,
        messageId: message.id,
        type: "rich",
        side,
        images: imageAttachments,
        text: isAttachmentPlaceholder ? "" : content,
        time,
        readState,
        canDelete,
      });
      continue;
    }

    if (content) {
      ensureDayLabel();
      const prescriptionId = String(metadata?.prescription_id || "").trim();
      const isPharmacyQuoteReady =
        String(metadata?.kind || "").trim() === "pharmacy_payment_requested" &&
        String(metadata?.status || "").trim() === "awaiting_payment" &&
        !["paid", "ready_for_pickup", "ready_for_delivery"].includes(fulfilledStatusByPrescription.get(prescriptionId)) &&
        prescriptionId;
      items.push({
        id: message.id,
        messageId: message.id,
        type: "text",
        side,
        text: content,
        time,
        readState,
        canDelete,
        action: isPharmacyQuoteReady
          ? {
              id: "fulfillment-pay-now",
              label: "Pay now",
              route: `/appointments/prescriptions/${encodeURIComponent(prescriptionId)}/pay`,
            }
          : null,
      });
    }
  }

  return items;
}

function buildFolaHistory(items, nextDraft) {
  const history = items
    .filter((item) => item?.type === "text")
    .slice(-8)
    .map((item) => ({
      role: item?.side === "right" ? "user" : "assistant",
      text: item?.text,
    }));

  history.push({ role: "user", text: nextDraft });
  return history;
}

function parseSourceConversationId(returnTo) {
  const match = String(returnTo || "").match(/^\/messages\/([^/?#]+)$/);
  if (!match) return "";
  const conversationId = String(match[1] || "").trim();
  if (!conversationId || conversationId === "fola") return "";
  return conversationId;
}

function clipText(value, maxLength = 180) {
  const text = String(value || "").replace(/\s+/g, " ").trim();
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 1).trimEnd()}…`;
}

function resolveFolaSourceContext({
  locationState = null,
  returnToState = null,
  sourceConversationId = "",
} = {}) {
  const merged = {
    ...(returnToState && typeof returnToState === "object" ? returnToState : {}),
    ...(locationState && typeof locationState === "object" ? locationState : {}),
  };

  const prescriptionId = String(merged?.prescriptionId || "").trim();
  const appointmentId = String(merged?.appointmentId || "").trim();
  const conversationId = String(sourceConversationId || "").trim();

  if (prescriptionId) {
    return {
      kind: "prescription_context",
      prescriptionId,
      appointmentId: appointmentId || null,
      conversationId: conversationId || null,
    };
  }

  if (appointmentId) {
    return {
      kind: "appointment_context",
      appointmentId,
      prescriptionId: null,
      conversationId: conversationId || null,
    };
  }

  return {
    kind: "message_thread",
    conversationId: conversationId || null,
    appointmentId: null,
    prescriptionId: null,
  };
}

function buildDoctorThreadSummaryCard({
  copy,
  threadTitle,
  messages,
  currentUserId,
  nowLabel = "Now",
  sourceContext = null,
}) {
  const doctorCopy = copy.doctorCards || {};
  const allMessages = Array.isArray(messages) ? messages : [];
  const nonEmptyMessages = allMessages.filter((message) =>
    String(message?.content || "").trim()
  );
  const patientMessages = nonEmptyMessages.filter(
    (message) => String(message?.sender_id || "").trim() !== currentUserId
  );
  const clinicianMessages = nonEmptyMessages.filter(
    (message) => String(message?.sender_id || "").trim() === currentUserId
  );
  const latestPatientMessage = patientMessages.at(-1);
  const latestClinicianMessage = clinicianMessages.at(-1);

  return {
    id: `fola-summary-${Date.now()}`,
    type: "fola-summary-card",
    side: "left",
    time: nowLabel,
    outputType: "draft",
    sourceContext,
    title: copy.actions.summarizeThread,
    threadTitle: threadTitle
      ? `${doctorCopy.sourceLabel || "Source"}: ${threadTitle}`
      : "",
    summary:
      latestPatientMessage
        ? `${doctorCopy.recentContextPrefix || "Recent patient context centres on"}: ${clipText(latestPatientMessage.content, 150)}`
        : doctorCopy.noStrongSummary ||
          "There is not enough patient detail in this thread yet to produce a stronger summary.",
    keyPointsTitle: doctorCopy.keyPointsTitle || "Key points",
    keyPoints: [
      patientMessages[0]
        ? `${doctorCopy.patientRaisedPrefix || "Patient raised"}: ${clipText(patientMessages[0].content, 110)}`
        : doctorCopy.noPatientMessage || "No patient message captured yet.",
      latestPatientMessage && latestPatientMessage !== patientMessages[0]
        ? `${doctorCopy.latestUpdatePrefix || "Latest update"}: ${clipText(latestPatientMessage.content, 110)}`
        : doctorCopy.noLaterPatientUpdate ||
          "No later patient update beyond the opening message.",
      latestClinicianMessage
        ? `${doctorCopy.clinicianResponsePrefix || "Clinician response so far"}: ${clipText(latestClinicianMessage.content, 110)}`
        : doctorCopy.noClinicianReply || "No clinician reply captured yet.",
    ].filter(Boolean),
    openItemsTitle: doctorCopy.openItemsTitle || "Open items",
    openItems: [
      latestPatientMessage
        ? doctorCopy.confirmTimeline ||
          "Confirm timeline, severity, and any red-flag symptoms."
        : doctorCopy.collectHistory ||
          "Collect patient history before finalising next steps.",
      doctorCopy.followUpBooking ||
        "Check whether a follow-up booking or escalation is needed.",
    ],
  };
}

function buildDoctorSoapCard({
  copy,
  threadTitle,
  messages,
  currentUserId,
  nowLabel = "Now",
  sourceContext = null,
}) {
  const doctorCopy = copy.doctorCards || {};
  const allMessages = Array.isArray(messages) ? messages : [];
  const patientMessages = allMessages.filter(
    (message) =>
      String(message?.sender_id || "").trim() !== currentUserId &&
      String(message?.content || "").trim()
  );
  const clinicianMessages = allMessages.filter(
    (message) =>
      String(message?.sender_id || "").trim() === currentUserId &&
      String(message?.content || "").trim()
  );

  return {
    id: `fola-soap-${Date.now()}`,
    type: "fola-soap-card",
    side: "left",
    time: nowLabel,
    outputType: "draft",
    sourceContext,
    title: copy.actions.draftSoap,
    threadTitle: threadTitle
      ? `${doctorCopy.sourceLabel || "Source"}: ${threadTitle}`
      : "",
    sections: [
      {
        label: doctorCopy.sections?.subjective || "Subjective",
        value:
          patientMessages.length > 0
            ? clipText(patientMessages.map((message) => message.content).join(" "), 220)
            : doctorCopy.limitedSymptoms ||
              "Patient-reported symptoms are still limited in this thread.",
      },
      {
        label: doctorCopy.sections?.objective || "Objective",
        value:
          doctorCopy.noObjectiveFindings ||
          "No objective findings, vitals, or exam results were documented in the message thread.",
      },
      {
        label: doctorCopy.sections?.assessment || "Assessment",
        value:
          doctorCopy.provisionalAssessment ||
          "Provisional messaging-only assessment. Needs clinician review against full history and any red flags.",
      },
      {
        label: doctorCopy.sections?.plan || "Plan",
        value:
          clinicianMessages.length > 0
            ? `${doctorCopy.planPrefix || "Continue with clinician follow-up and clarify"}: ${clipText(clinicianMessages.at(-1)?.content, 140)}`
            : doctorCopy.planFallback ||
              "Clarify severity, duration, and next safe step. Consider booking or escalation if risk increases.",
      },
    ],
    subjective:
      patientMessages.length > 0
        ? clipText(patientMessages.map((message) => message.content).join(" "), 220)
        : doctorCopy.limitedSymptoms ||
          "Patient-reported symptoms are still limited in this thread.",
    objective:
      doctorCopy.noObjectiveFindings ||
      "No objective findings, vitals, or exam results were documented in the message thread.",
    assessment:
      doctorCopy.provisionalAssessment ||
      "Provisional messaging-only assessment. Needs clinician review against full history and any red flags.",
    plan:
      clinicianMessages.length > 0
        ? `${doctorCopy.planPrefix || "Continue with clinician follow-up and clarify"}: ${clipText(clinicianMessages.at(-1)?.content, 140)}`
        : doctorCopy.planFallback ||
          "Clarify severity, duration, and next safe step. Consider booking or escalation if risk increases.",
    confidenceNote:
      doctorCopy.confidenceNote ||
      "Draft from message content only. Review before saving or sharing clinically.",
  };
}

function buildDoctorReplyCard({
  copy,
  threadTitle,
  messages,
  currentUserId,
  nowLabel = "Now",
  sourceContext = null,
}) {
  const doctorCopy = copy.doctorCards || {};
  const allMessages = Array.isArray(messages) ? messages : [];
  const latestPatientMessage = [...allMessages]
    .reverse()
    .find(
      (message) =>
        String(message?.sender_id || "").trim() !== currentUserId &&
        String(message?.content || "").trim()
    );

  const responseBody = latestPatientMessage
    ? `${doctorCopy.replyPrefix || "Thank you for the update. I have noted that"} ${clipText(
        latestPatientMessage.content,
        140
      )}. ${doctorCopy.replySuffix || "Please keep monitoring your symptoms and let me know if anything worsens, especially any breathing difficulty, severe pain, or rapid change."}`
    : doctorCopy.replyFallback ||
      "Thank you for the update. I have reviewed the thread and will guide the next safe step once a little more detail is available.";

  return {
    id: `fola-reply-${Date.now()}`,
    type: "fola-reply-card",
    side: "left",
    time: nowLabel,
    outputType: "draft",
    sourceContext,
    title: copy.actions.draftReply,
    threadTitle: threadTitle
      ? `${doctorCopy.sourceLabel || "Source"}: ${threadTitle}`
      : "",
    body: responseBody,
    supportingText:
      doctorCopy.replySupportText ||
      "Edit for tone and clinical precision before sending.",
    cta: {
      id: "doctor-use-draft-reply",
      label: doctorCopy.useAsDraft || "Use as draft",
    },
  };
}

function getLegacyFolaDecisionPromptIndex(items) {
  for (let index = items.length - 1; index >= 0; index -= 1) {
    const item = items[index];
    if (item?.type !== "text" || item?.side !== "left") continue;

    const normalized = String(item.text || "").toLowerCase();
    if (
      normalized.includes("would you like me to help you find a nearby health cent") ||
      normalized.includes("would you like me to help you find a nearby health centre")
    ) {
      return index;
    }
  }

  return -1;
}

function deriveFolaOptions(items, activePrompt = null, copy = null) {
  if (Array.isArray(activePrompt?.choices) && activePrompt.choices.length > 0) {
    return activePrompt.choices;
  }

  const promptIndex = getLegacyFolaDecisionPromptIndex(items);
  if (promptIndex < 0) return [];

  const userReply = items
    .slice(promptIndex + 1)
    .find((item) => item?.type === "text" && item?.side === "right");

  if (userReply) return [];
  return [
    {
      id: "legacy-yes",
      label: copy?.actions?.yes || "Yes",
      variant: "default",
    },
    {
      id: "legacy-no",
      label: copy?.actions?.no || "No",
      variant: "tonal",
    },
  ];
}

function deriveFolaActionMode(items, activePrompt = null, yesLabels = []) {
  if (activePrompt?.promptKey === "patient-appointments-routing") {
    return "booking";
  }

  if (Array.isArray(activePrompt?.choices) && activePrompt.choices.length > 0) {
    return activePrompt.layout === "grid" ? "prompt-grid" : "decision";
  }

  const promptIndex = getLegacyFolaDecisionPromptIndex(items);
  if (promptIndex < 0) return "default";

  const userReply = items
    .slice(promptIndex + 1)
    .find((item) => item?.type === "text" && item?.side === "right");

  if (!userReply) return "decision";
  const normalizedReply = String(userReply.text || "").trim().toLowerCase();
  const normalizedYesLabels = yesLabels
    .map((label) => String(label || "").trim().toLowerCase())
    .filter(Boolean);
  if (normalizedReply === "yes" || normalizedYesLabels.includes(normalizedReply)) {
    return "booking";
  }
  return "default";
}

function getReusableFolaPrompt(items, promptKey) {
  const allItems = Array.isArray(items) ? items : [];
  const key = String(promptKey || "").trim();
  if (!key) return null;

  for (let index = allItems.length - 1; index >= 0; index -= 1) {
    const item = allItems[index];
    if (item?.type === "fola-prompt" && item?.promptKey === key) {
      return item;
    }
  }

  return null;
}

const LOCAL_FOLA_REPLY_DELAY_MS = 900;
const LOCAL_FOLA_REVEAL_INTERVAL_MS = 18;
const LOCAL_FOLA_REVEAL_CHARS_PER_TICK = 7;

function waitForLocalFolaReply() {
  return new Promise((resolve) => {
    window.setTimeout(resolve, LOCAL_FOLA_REPLY_DELAY_MS);
  });
}

function waitForLocalFolaRevealTick() {
  return new Promise((resolve) => {
    window.setTimeout(resolve, LOCAL_FOLA_REVEAL_INTERVAL_MS);
  });
}

function waitForPharmacyGreetingPause() {
  return new Promise((resolve) => {
    window.setTimeout(resolve, 360);
  });
}

function HeaderIconButton({ label, icon, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="flex h-11 w-11 items-center justify-center rounded-[12px]"
    >
      {children || <img src={icon} alt="" className="h-6 w-6" draggable="false" />}
    </button>
  );
}

function IndeterminateProgressIndicator() {
  const [step, setStep] = React.useState(0);

  React.useEffect(() => {
    const timer = window.setInterval(() => {
      setStep((current) => (current + 1) % 3);
    }, 240);

    return () => window.clearInterval(timer);
  }, []);

  return (
    <span className="relative block h-6 w-6" aria-hidden="true">
      {[0, 1, 2].map((segment) => (
        <svg
          key={segment}
          viewBox="0 0 24 24"
          className={`absolute inset-0 transition-opacity duration-150 ${
            step === segment ? "opacity-100" : "opacity-35"
          }`}
          style={{ transform: `rotate(${segment * 120}deg)` }}
        >
          <path
            d="M12 3.75a8.25 8.25 0 0 0-5.84 2.41"
            fill="none"
            stroke="#4DA3FF"
            strokeWidth="2.2"
            strokeLinecap="round"
          />
        </svg>
      ))}
    </span>
  );
}

function ConversationTopBar({
  title,
  onBack,
  onOpenFola,
  onReset,
  backLabel = "Back to messages",
  openFolaLabel = "Open Fola conversation",
  composeLabel = "Compose message",
}) {
  return (
    <div className="relative shrink-0 bg-[rgb(var(--clr-surface-cont-highest))] pt-4 pb-4">
      <div className="flex h-16 w-full items-center justify-between gap-4">
        <button
          type="button"
          onClick={onBack}
          className="flex h-11 w-11 items-center justify-center rounded-[12px] bg-[rgba(86,106,139,0.08)]"
          aria-label={backLabel}
        >
          <img src={ICON_BACK} alt="" className="h-5 w-5" draggable="false" />
        </button>

        <div className="min-w-0 flex-1">
          <h1 className="truncate text-[20px] font-normal leading-7 tracking-[-0.48px] text-[rgb(var(--clr-on-surface))]">
            {title}
          </h1>
        </div>

        <div className="flex items-center gap-[2px]">
          <HeaderIconButton label={openFolaLabel} icon={ICON_FOLA} onClick={onOpenFola}>
            <img src={ICON_FOLA} alt="" className={FOLA_ICON_SIZE} draggable="false" />
          </HeaderIconButton>
          <HeaderIconButton label={composeLabel} icon={ICON_WRITE} onClick={onReset} />
        </div>
      </div>

      <div className="pointer-events-none mt-4 h-px w-full bg-[rgba(213,215,220,0.18)] md:mt-0" />
    </div>
  );
}

function MessageContext({ time, align = "start", readState = null }) {
  const { t } = useLanguage();
  const statusIcon =
    readState === "read" ? ICON_DONE_ALL : readState === "sent" ? ICON_DONE : "";

  return (
    <div
      className={`mt-1 flex items-center gap-1 py-1 ${
        align === "end" ? "justify-end" : "justify-start"
      }`}
    >
      {statusIcon ? (
        <img src={statusIcon} alt={readState === "read" ? t("messages.receipt.read", "Read") : t("messages.receipt.sent", "Sent")} className="h-[18px] w-[18px]" draggable="false" />
      ) : null}
      <span className="text-[12px] font-medium leading-4 tracking-[-0.12px] text-[#A8B3C4]">
        {time}
      </span>
    </div>
  );
}

function MessageDeleteDialog({
  open,
  busyAction = "",
  onClose,
  onDeleteForMe,
  onDeleteForBoth,
  closeLabel = "Close delete dialog",
  title = "Delete message?",
  body = "Delete this message for you only, or remove it for both participants. This action cannot be undone.",
  deletingLabel = "Deleting...",
  deleteForMeLabel = "Delete for me",
  deleteForBothLabel = "Delete for both",
}) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[140] flex items-center justify-center bg-[rgba(4,8,18,0.45)] px-5">
      <button
        type="button"
        aria-label={closeLabel}
        className="absolute inset-0"
        onClick={busyAction ? undefined : onClose}
      />

      <div className="relative w-full max-w-[312px] overflow-hidden rounded-[28px] bg-white shadow-[0px_0px_0px_1px_rgba(24,50,119,0.10),0px_1.5px_0px_-0.5px_rgba(26,27,35,0.06),0px_3px_3px_-1px_rgba(26,27,35,0.06),0px_3px_6px_-2px_rgba(26,27,35,0.06),0px_6px_12px_-4px_rgba(29,79,215,0.06),0px_12px_24px_-10px_rgba(29,79,215,0.06)]">
        <div className="flex flex-col items-center gap-4 px-6 pt-6 text-center">
          <img
            src={ICON_DELETE_BACKGROUND_CRITICAL}
            alt=""
            aria-hidden="true"
            className="h-11 w-11"
            draggable="false"
          />
          <h2 className="text-[24px] font-normal leading-8 tracking-[-0.576px] text-[rgb(var(--clr-on-surface))]">
            {title}
          </h2>
          <p className="mt-4 text-[14px] font-normal leading-5 tracking-[-0.336px] text-[rgb(var(--clr-on-surface-variant))]">
            {body}
          </p>
        </div>

        <div className="flex items-center gap-2 p-6">
          <button
            type="button"
            onClick={onDeleteForMe}
            disabled={Boolean(busyAction)}
            className="flex min-h-12 flex-1 items-center justify-center rounded-[12px] bg-[rgba(86,106,139,0.08)] px-6 py-3 text-[16px] font-medium leading-5 tracking-[-0.384px] text-[rgb(var(--afrimed-primary))] disabled:opacity-60"
          >
            {busyAction === "mine" ? deletingLabel : deleteForMeLabel}
          </button>

          <button
            type="button"
            onClick={onDeleteForBoth}
            disabled={Boolean(busyAction)}
            className="flex min-h-12 flex-1 items-center justify-center rounded-[12px] bg-[rgba(248,83,58,0.08)] px-6 py-3 text-[16px] font-medium leading-5 tracking-[-0.384px] text-[rgb(var(--schemes-critical))] disabled:opacity-60"
          >
            {busyAction === "both" ? deletingLabel : deleteForBothLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function MessageToast({ toast, onClose, dismissLabel = "OK" }) {
  if (!toast?.message) return null;

  return (
    <div
      className="fixed left-1/2 z-[135] w-full max-w-[376px] -translate-x-1/2 px-4 bottom-[calc(env(safe-area-inset-bottom)+5.5rem)] md:bottom-6"
      role="status"
      aria-live="polite"
    >
      <div className="flex min-h-12 items-center gap-1 rounded-[10px] bg-[#040812] pl-4 pr-2 py-1 shadow-[0px_0px_0px_1px_rgba(24,50,119,0.10),0px_1.5px_0px_-0.5px_rgba(26,27,35,0.06),0px_3px_3px_-1px_rgba(26,27,35,0.06),0px_3px_6px_-2px_rgba(26,27,35,0.06),0px_6px_12px_-4px_rgba(29,79,215,0.06),0px_12px_24px_-10px_rgba(29,79,215,0.06),0px_12px_36px_-6px_rgba(26,27,35,0.06)]">
        <div className="min-w-0 flex-1 truncate text-[14px] font-normal leading-5 tracking-[-0.336px] text-[#F0F1F3]">
          {toast.message}
        </div>
        {toast.actionLabel ? (
          <button
            type="button"
            onClick={toast.onAction}
            className="shrink-0 rounded-[100px] px-3 py-2 text-[16px] font-medium leading-5 tracking-[-0.384px] text-[#1A8CFF]"
          >
            {toast.actionLabel}
          </button>
        ) : null}
        {!toast.actionLabel ? (
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-[100px] px-3 py-2 text-[16px] font-medium leading-5 tracking-[-0.384px] text-[#1A8CFF]"
          >
            {dismissLabel}
          </button>
        ) : null}
      </div>
    </div>
  );
}

function MessageBubble({ item, onOpenActions, onFolaAction }) {
  const pressTimerRef = React.useRef(0);
  const desktopDeleteHint = item?.canDelete ? "Right-click to delete message" : undefined;
  const interactiveBubbleClass = item?.canDelete ? "cursor-context-menu" : "";
  const clearPressTimer = React.useCallback(() => {
    if (pressTimerRef.current) {
      window.clearTimeout(pressTimerRef.current);
      pressTimerRef.current = 0;
    }
  }, []);
  const openActions = React.useCallback(() => {
    if (!item?.canDelete) return;
    onOpenActions?.(item);
  }, [item, onOpenActions]);

  const interactionProps = item?.canDelete
    ? {
        onContextMenu: (event) => {
          event.preventDefault();
          openActions();
        },
        onDoubleClick: openActions,
        onTouchStart: () => {
          clearPressTimer();
          pressTimerRef.current = window.setTimeout(() => {
            openActions();
            pressTimerRef.current = 0;
          }, 420);
        },
        onTouchEnd: clearPressTimer,
        onTouchMove: clearPressTimer,
        onTouchCancel: clearPressTimer,
      }
    : {};

  React.useEffect(() => clearPressTimer, [clearPressTimer]);

  if (item.type === "date") {
    return (
      <div className="w-full pt-1 text-center text-body-medium text-on-surface-var">
        {item.label}
      </div>
    );
  }

  if (item.type === "fola-prompt") {
    return null;
  }

  if (["fola-booking-services", "fola-booking-options", "fola-booking-decision", "fola-booking-confirmation"].includes(item.type)) {
    return <div className="flex w-full justify-start"><FolaBookingAction item={item} onAction={onFolaAction} /></div>;
  }

  if (
    [
      "fola-notice",
      "fola-summary-card",
      "fola-soap-card",
      "fola-reply-card",
    ].includes(item.type)
  ) {
    return (
      <div className="flex w-full justify-start">
        <div className="flex max-w-[420px] flex-col md:max-w-[560px]">
          <FolaCardRenderer item={item} onSelectChoice={onFolaAction} />
          <MessageContext time={item.time} align="start" />
        </div>
      </div>
    );
  }

  if (item.type === "image") {
    return (
      <div
        className={`flex w-full ${item.side === "right" ? "justify-end" : "justify-start"} ${interactiveBubbleClass}`}
        title={desktopDeleteHint}
        {...interactionProps}
      >
        <div className="max-w-[316px] md:max-w-[520px]">
          <div className="overflow-hidden rounded-[16px] shadow-[0_0_0_1px_rgba(24,50,119,0.10),0_3px_3px_-1.5px_rgba(26,27,35,0.06),0_1.5px_0_-0.5px_rgba(26,27,35,0.06)]">
            <img
              src={item.src}
              alt="Attachment"
              className="block h-[205px] w-full object-cover"
              draggable="false"
            />
          </div>
          <MessageContext
            time={item.time}
            align={item.side === "right" ? "end" : "start"}
            readState={item.readState}
          />
        </div>
      </div>
    );
  }

  if (item.type === "rich") {
    const isRight = item.side === "right";

    return (
      <div
        className={`flex w-full ${isRight ? "justify-end" : "justify-start"} ${interactiveBubbleClass}`}
        title={desktopDeleteHint}
        {...interactionProps}
      >
        <div className="max-w-[316px] md:max-w-[520px]">
          <div
            className={[
              "overflow-hidden shadow-[0_0_0_1px_rgba(24,50,119,0.10),0_1.5px_0_-0.5px_rgba(26,27,35,0.06),0_3px_3px_-1.5px_rgba(26,27,35,0.06)]",
              isRight
                ? "rounded-bl-[16px] rounded-br-[4px] rounded-tl-[16px] rounded-tr-[16px] bg-[#DBF4FF]"
                : "rounded-bl-[4px] rounded-br-[16px] rounded-tl-[16px] rounded-tr-[16px] bg-[rgb(var(--clr-surface-container-low))]",
            ].join(" ")}
          >
            {Array.isArray(item.images)
              ? item.images.map((src, index) => (
                  <img
                    key={`${item.id}-image-${index}`}
                    src={src}
                    alt="Attachment"
                    className={[
                      "block h-[205px] w-full object-cover",
                      item.text && index < item.images.length - 1
                        ? "border-b border-[rgba(24,50,119,0.08)]"
                        : "",
                    ].join(" ")}
                    draggable="false"
                  />
                ))
              : null}
            {item.text ? (
              <div
                className={[
                  "px-3 py-3 text-[14px] font-normal leading-5 tracking-[-0.336px]",
                  isRight ? "text-[#112238]" : "text-[rgb(var(--clr-on-surface))]",
                ].join(" ")}
              >
                <SafeMessageContent value={item.text} />
              </div>
            ) : null}
          </div>
          <MessageContext
            time={item.time}
            align={isRight ? "end" : "start"}
            readState={item.readState}
          />
        </div>
      </div>
    );
  }

  if (item.type === "deleted") {
    const isRight = item.side === "right";

    return (
      <div className={`flex w-full ${isRight ? "justify-end" : "justify-start"}`}>
        <div className="flex max-w-[240px] flex-col md:max-w-[420px]">
          <div className="rounded-[16px] border border-[rgba(86,106,139,0.10)] bg-[rgba(86,106,139,0.08)] px-3 py-2 text-[14px] italic leading-5 tracking-[-0.336px] text-[rgb(var(--clr-on-surface-variant))]">
            {item.text}
          </div>
          <MessageContext time={item.time} align={isRight ? "end" : "start"} />
        </div>
      </div>
    );
  }

  const isRight = item.side === "right";
  const shouldFormatFolaText =
    !isRight &&
    ["support", "draft", "admin"].includes(String(item.outputType || "").trim());

  return (
    <div
      className={`flex w-full ${isRight ? "justify-end" : "justify-start"} ${interactiveBubbleClass}`}
      title={desktopDeleteHint}
      {...interactionProps}
    >
      <div className="flex max-w-[240px] flex-col md:max-w-[560px]">
        <div
          className={[
            "px-3 py-2 text-[14px] font-normal leading-5 tracking-[-0.336px]",
            isRight ? "text-[#112238]" : "text-[rgb(var(--clr-on-surface))]",
            "shadow-[0_0_0_1px_rgba(24,50,119,0.10),0_1.5px_0_-0.5px_rgba(26,27,35,0.06),0_3px_3px_-1.5px_rgba(26,27,35,0.06)]",
            isRight
              ? "rounded-bl-[16px] rounded-br-[4px] rounded-tl-[16px] rounded-tr-[16px] bg-[#DBF4FF]"
              : "rounded-bl-[4px] rounded-br-[16px] rounded-tl-[16px] rounded-tr-[16px] bg-[rgb(var(--clr-surface-container-low))]",
          ].join(" ")}
        >
          <SafeMessageContent
            value={item.text}
            className={shouldFormatFolaText ? "space-y-2" : ""}
          />
          {item?.action?.id && item?.action?.label ? (
            <Button
              variant="text"
              size="xs"
              onClick={() => onFolaAction?.(item.action)}
              className="mt-3"
            >
              {item.action.label}
            </Button>
          ) : null}
        </div>
        <MessageContext
          time={item.time}
          align={isRight ? "end" : "start"}
          readState={item.readState}
        />
      </div>
    </div>
  );
}

function Composer({
  isFolaConversation,
  thinking,
  draft,
  pendingAttachment,
  disabled,
  actionMode,
  placeholder,
  thinkingPlaceholder,
  selectedOptions,
  optionChoices,
  onToggleOption,
  onDraftChange,
  onAttach,
  onSend,
  onSelectPromptChoice,
  draftStorageKey,
  languageCode = "en-GB",
  allLabel = "All",
  attachLabel = "Attach photo",
  sendLabel = "Send message",
  thinkingLabel = "Fola is thinking",
  selectedAttachmentAlt = "Selected attachment",
}) {
  const hasOptionList =
    isFolaConversation && actionMode === "decision" && optionChoices.length > 0;
  const showPromptButtons =
    isFolaConversation &&
    (actionMode === "prompt-grid" || actionMode === "booking") &&
    optionChoices.length > 0;
  const promptControlsVisible = hasOptionList || showPromptButtons;
  const [promptPanelExpanded, setPromptPanelExpanded] = React.useState(() => {
    if (typeof window === "undefined") return true;
    return window.innerWidth >= 768;
  });

  React.useEffect(() => {
    if (!promptControlsVisible) return;
    if (typeof window === "undefined") return;
    if (window.innerWidth < 768) {
      setPromptPanelExpanded(false);
      return;
    }
    setPromptPanelExpanded(true);
  }, [promptControlsVisible, actionMode, optionChoices.length]);

  const showExpandedPromptContent = promptControlsVisible && promptPanelExpanded;
  const canTogglePromptPanel = promptControlsVisible;

  return (
    <div>
      <div className="flex items-end gap-2 px-2 pb-4 pt-3 md:px-4">
        {isFolaConversation ? (
          <div className="mb-5 flex h-6 w-6 items-center justify-center">
            {thinking ? (
              <IndeterminateProgressIndicator />
            ) : (
              <img src={ICON_FOLA} alt="" className={FOLA_ICON_SIZE} draggable="false" />
            )}
          </div>
        ) : null}

        <div className="min-w-0 flex-1 rounded-[16px] border border-[rgba(var(--afrimed-primary),0.08)] bg-[rgb(var(--clr-surface))] px-4 py-2 shadow-[0_0_0_3px_rgba(66,95,255,0.3)] focus-within:border-[rgba(var(--afrimed-primary),0.22)] focus-within:shadow-[0_0_0_3px_rgba(66,95,255,0.3)]">
          {pendingAttachment?.previewUrl ? (
            <div className="mb-4 overflow-hidden rounded-[16px] shadow-[0_0_0_1px_rgba(24,50,119,0.10),0_3px_3px_-1.5px_rgba(26,27,35,0.06),0_1.5px_0_-0.5px_rgba(26,27,35,0.06)]">
              <img
                src={pendingAttachment.previewUrl}
                alt={pendingAttachment.fileName || selectedAttachmentAlt}
                className="block h-[205px] w-full object-cover"
                draggable="false"
              />
            </div>
          ) : null}

          {showPromptButtons && showExpandedPromptContent ? (
            <>
              <div className="mb-4 grid grid-cols-2 gap-3">
                {optionChoices.map((choice) => (
                  <button
                    key={choice.id}
                    type="button"
                    onClick={() => onSelectPromptChoice(choice)}
                    disabled={disabled}
                    className={[
                      "min-h-12 rounded-[12px] px-4 py-3 text-[14px] font-medium leading-5 tracking-[-0.336px]",
                      choice.variant === "default"
                        ? "bg-[rgb(var(--afrimed-primary))] text-[rgb(var(--afrimed-on-primary))]"
                        : choice.variant === "tonal"
                          ? "bg-[var(--state-primary-12)] text-[rgb(var(--afrimed-primary))]"
                          : "bg-[rgba(86,106,139,0.08)] text-[rgb(var(--afrimed-primary))]",
                    ].join(" ")}
                  >
                    {choice.label}
                  </button>
                ))}
              </div>

              <div className="my-3 h-px w-full bg-[rgba(213,215,220,0.18)]" />
            </>
          ) : null}

          {hasOptionList && showExpandedPromptContent ? (
            <>
              <div className="space-y-2">
                {optionChoices.map((choice) => {
                  const checked = selectedOptions.includes(choice.id);

                  return (
                    <label
                      key={choice.id}
                      className="flex items-start gap-1 text-[16px] font-normal leading-normal text-[rgb(var(--clr-on-surface))]"
                    >
                      <Checkbox
                        checked={checked}
                        onChange={() => onToggleOption(choice.id)}
                        className="mt-[2px] h-6 w-6 rounded-[4px]"
                      />
                      <span>{choice.label}</span>
                    </label>
                  );
                })}
              </div>

              <div className="my-3 h-px w-full bg-[rgba(213,215,220,0.18)]" />
            </>
          ) : null}

          <MessageComposerField
            bare
            value={draft}
            lang={languageCode}
            draftStorageKey={draftStorageKey}
            onChange={onDraftChange}
            onKeyDown={(event) => {
              // The shared composer handles desktop Enter / Shift+Enter and
              // leaves mobile return keys as natural multiline entry.
            }}
            onSend={onSend}
            disabled={thinking || disabled}
            placeholder={
              thinking && isFolaConversation
                ? thinkingPlaceholder
                : placeholder
            }
            ariaLabel={isFolaConversation ? "Ask Fola anything" : placeholder}
          />

          <div className="mt-3 flex items-center justify-between">
            {isFolaConversation ? (
              <button
                type="button"
                onClick={() => {
                  if (!canTogglePromptPanel) return;
                  setPromptPanelExpanded((current) => !current);
                }}
                className="flex items-center gap-[4px] disabled:opacity-60"
                disabled={!canTogglePromptPanel || disabled}
                aria-expanded={showExpandedPromptContent}
              >
                <img
                  src={ICON_CHEVRON}
                  alt=""
                  className={[
                    "h-6 w-6 transition-transform duration-200",
                    showExpandedPromptContent ? "rotate-0" : "rotate-180",
                  ].join(" ")}
                  draggable="false"
                />
                <span className="text-[16px] font-medium leading-6 tracking-[-0.384px] text-[rgb(var(--clr-on-surface-variant))]">
                  {allLabel}
                </span>
              </button>
            ) : (
              <div className="h-6 w-6" aria-hidden="true" />
            )}

            <div className="flex items-center gap-[6px]">
              {!thinking || !isFolaConversation ? (
                <button
                  type="button"
                  onClick={onAttach}
                  disabled={thinking || disabled}
                  className="flex h-11 w-11 items-center justify-center rounded-[12px] disabled:opacity-50"
                  aria-label={attachLabel}
                >
                  <img src={ICON_ATTACH} alt="" className="h-6 w-6" draggable="false" />
                </button>
              ) : null}

              <button
                type="button"
                onClick={thinking ? undefined : onSend}
                disabled={
                  disabled ||
                  thinking
                }
                className={[
                  "flex h-11 w-11 items-center justify-center rounded-[12px]",
                  thinking && isFolaConversation
                    ? "bg-[#CCDDFF]"
                    : "bg-[rgb(var(--afrimed-primary))] disabled:bg-[rgba(29,78,216,0.26)]",
                ].join(" ")}
                aria-label={thinking && isFolaConversation ? thinkingLabel : sendLabel}
              >
                <img
                  src={thinking && isFolaConversation ? ICON_STOP : ICON_SEND}
                  alt=""
                  className="h-5 w-5"
                  draggable="false"
                />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ConversationPage() {
  const navigate = useNavigate();
  const { languageCode, t } = useLanguage();
  const { conversationId = "" } = useParams();
  const location = useLocation();
  const auth = (typeof useAuth === "function" ? useAuth() : {}) || {};
  const currentUserId = String(auth?.user?.id || "").trim();
  const currentRole = String(
    auth?.profile?.role || auth?.user?.user_metadata?.role || ""
  ).toLowerCase();
  const folaRole =
    currentRole === "doctor"
      ? "doctor"
      : currentRole === "pharmacy" || location.pathname.startsWith("/pharmacy/messages")
        ? "pharmacy"
        : "patient";
  const fileInputRef = React.useRef(null);
  const timeline = useMessageTimelineScroll(`${conversationId}:${currentUserId}`);
  const timelineRef = timeline.timelineRef;

  const initialTitle = formatConversationTitle(conversationId, location.state || {}, {
    fallbackTitle: t("messages.inbox.fallbackTitle", "Conversation"),
    folaTitle: "Fola",
  });
  const locationConversationTitle = String(location.state?.conversationTitle || "").trim();
  const messagesBasePath = location.pathname.startsWith("/pharmacy/messages")
    ? "/pharmacy/messages"
    : "/messages";
  const isPharmacyMessagesContext = messagesBasePath === "/pharmacy/messages";
  const returnTo = String(location.state?.returnTo || "").trim();
  const promptKeyFromQuery = String(new URLSearchParams(location.search || "").get("prompt") || "").trim();
  const initialDraftFromState = String(
    location.state?.initialDraft ||
      (isPharmacyMessagesContext ? getPharmacyFolaPrompt(promptKeyFromQuery) : "") ||
      "",
  ).trim();
  const shouldAutoSubmitPharmacyPrompt =
    isPharmacyMessagesContext && folaRole === "pharmacy" && !!promptKeyFromQuery && !!initialDraftFromState;
  const shouldTypePharmacyDirectOpening =
    isPharmacyMessagesContext &&
    folaRole === "pharmacy" &&
    conversationId === "fola" &&
    !promptKeyFromQuery;
  const returnToState =
    location.state && typeof location.state.returnToState === "object"
      ? location.state.returnToState
      : null;
  const routeConversationType = String(location.state?.conversationType || "").trim().toLowerCase();
  const isExplicitGroupConversation = routeConversationType === "group";
  const isExplicitFulfillmentConversation =
    routeConversationType === "fola" || routeConversationType === "fulfillment";
  const blocksDirectFallback =
    isExplicitGroupConversation || isExplicitFulfillmentConversation;
  const [resolvedTitle, setResolvedTitle] = React.useState(initialTitle);
  const [resolvedConversationId, setResolvedConversationId] = React.useState(
    conversationId === "fola" ? "fola" : null
  );
  const [resolvedConversationDescription, setResolvedConversationDescription] = React.useState("");
  const [items, setItems] = React.useState([]);
  const [draft, setDraft] = React.useState("");
  const [selectedOptions, setSelectedOptions] = React.useState([]);
  const [folaPreferences, setFolaPreferences] = React.useState(() =>
    readAccessibilityFolaPrefs()
  );
  const [myEhrFolaContext, setMyEhrFolaContext] = React.useState(null);
  const [governedFolaConversationId, setGovernedFolaConversationId] = React.useState("");
  const [governedFolaStage, setGovernedFolaStage] = React.useState("");
  const [folaResumeComplete, setFolaResumeComplete] = React.useState(false);
  const [thinking, setThinking] = React.useState(false);
  const [folaRevealing, setFolaRevealing] = React.useState(false);
  const [loading, setLoading] = React.useState(conversationId !== "fola");
  const [threadError, setThreadError] = React.useState("");
  const [threadRefreshError, setThreadRefreshError] = React.useState("");
  const [pendingAttachment, setPendingAttachment] = React.useState(null);
  const [hiddenMessageIds, setHiddenMessageIds] = React.useState([]);
  const [deleteTarget, setDeleteTarget] = React.useState(null);
  const [deleteBusyAction, setDeleteBusyAction] = React.useState("");
  const [toast, setToast] = React.useState({ message: "", actionLabel: "", onAction: null });
  const [pendingDeleteTargetMessageId, setPendingDeleteTargetMessageId] = React.useState("");
  const [hasOlderMessages, setHasOlderMessages] = React.useState(false);
  const [loadingOlderMessages, setLoadingOlderMessages] = React.useState(false);
  const liveMessageOffsetRef = React.useRef(0);
  const threadLoadSequenceRef = React.useRef(0);
  const preferredLanguageCode = languageCode;
  const [sourceThreadContext, setSourceThreadContext] = React.useState(null);
  const localRevealSequenceRef = React.useRef(0);
  const sendInFlightRef = React.useRef(false);
  const hiddenMessageIdsRef = React.useRef(new Set());
  const lastOpenedConversationIdRef = React.useRef("");
  const autoSubmittedPharmacyPromptRef = React.useRef("");
  const typedPharmacyOpeningRef = React.useRef("");
  const cachedSnapshot = React.useMemo(
    () => readConversationSnapshot(currentUserId, conversationId),
    [conversationId, currentUserId]
  );
  const isFolaConversation = conversationId === "fola";
  const currentDraftStorageKey = composerDraftKey({
    kind: isFolaConversation ? "fola" : "message",
    role: currentRole,
    userId: currentUserId,
    conversationId: isFolaConversation ? (governedFolaConversationId || "new") : conversationId,
  });
  const sourceConversationId = React.useMemo(
    () => parseSourceConversationId(returnTo),
    [returnTo]
  );
  const folaSourceContext = React.useMemo(
    () => {
      const baseContext = resolveFolaSourceContext({
        locationState: location.state || null,
        returnToState,
        sourceConversationId,
      });
      if (!myEhrFolaContext) return baseContext;
      return {
        ...baseContext,
        myEhr: myEhrFolaContext,
      };
    },
    [location.state, myEhrFolaContext, returnToState, sourceConversationId]
  );
  const activeFolaPrompt = React.useMemo(
    () => (isFolaConversation ? getActiveFolaPrompt(items) : null),
    [isFolaConversation, items]
  );
  const effectiveFolaPrompt = React.useMemo(() => {
    if (activeFolaPrompt) return activeFolaPrompt;
    if (!isFolaConversation || folaRole !== "pharmacy") return null;
    return getReusableFolaPrompt(items, "pharmacy-starter");
  }, [activeFolaPrompt, folaRole, isFolaConversation, items]);
  const isPersistedFulfillmentConversation = React.useMemo(
    () => isFulfillmentConversationDescription(resolvedConversationDescription),
    [resolvedConversationDescription]
  );
  React.useEffect(() => {
    let alive = true;

    async function hydrateMyEhrContext() {
      if (!isFolaConversation || folaRole !== "patient" || !currentUserId) {
        setMyEhrFolaContext(null);
        return;
      }

      const nextContext = await loadLatestVitalRepPatientContext(currentUserId);
      if (alive) setMyEhrFolaContext(nextContext);
    }

    hydrateMyEhrContext();
    return () => {
      alive = false;
    };
  }, [currentUserId, folaRole, isFolaConversation]);

  const folaUiCopy = React.useMemo(
    () => getFolaUiCopy(preferredLanguageCode),
    [preferredLanguageCode]
  );
  const conversationCopy = React.useMemo(
    () => ({
      now: t("messages.conversation.now", "Now"),
      deletedForMe: t("messages.conversation.deletedForMe", "You deleted this message"),
      deletedBySender: t(
        "messages.conversation.deletedBySender",
        "Sender deleted message"
      ),
      deleteDialog: {
        close: t("messages.conversation.deleteDialog.close", "Close delete dialog"),
        title: t("messages.conversation.deleteDialog.title", "Delete message?"),
        body: t(
          "messages.conversation.deleteDialog.body",
          "Delete this message for you only, or remove it for both participants. This action cannot be undone."
        ),
        deleting: t("messages.conversation.deleteDialog.deleting", "Deleting..."),
        deleteForMe: t("messages.conversation.deleteDialog.forMe", "Delete for me"),
        deleteForBoth: t("messages.conversation.deleteDialog.forBoth", "Delete for both"),
      },
      toast: {
        dismiss: t("messages.conversation.toast.dismiss", "OK"),
        undo: t("messages.conversation.toast.undo", "Undo"),
        deletedForMe: t("messages.conversation.toast.deletedForMe", "Message deleted for me"),
        deletedForBoth: t(
          "messages.conversation.toast.deletedForBoth",
          "Message deleted for both"
        ),
        deleteError: t(
          "messages.conversation.toast.deleteError",
          "Could not delete the message right now."
        ),
      },
      composer: {
        all: t("messages.conversation.composer.all", "All"),
        attach: t("messages.conversation.composer.attach", "Attach photo"),
        send: t("messages.conversation.composer.send", "Send message"),
        thinking: t("messages.conversation.composer.thinking", "Fola is thinking"),
        selectedAttachmentAlt: t(
          "messages.conversation.composer.selectedAttachmentAlt",
          "Selected attachment"
        ),
      },
      topBar: {
        back: t("messages.conversation.topBar.back", "Back to messages"),
        openFola: t(
          "messages.conversation.topBar.openFola",
          "Open Fola conversation"
        ),
        compose: t("messages.conversation.topBar.compose", "Compose message"),
      },
      sourceThreadFallback: t(
        "messages.conversation.sourceThreadFallback",
        "Source thread"
      ),
      errors: {
        resolve: t(
          "messages.conversation.errors.resolve",
          "Could not resolve this conversation."
        ),
        load: t(
          "messages.conversation.errors.load",
          "Could not load this conversation."
        ),
        send: t(
          "messages.conversation.errors.send",
          "Could not send the message."
        ),
      },
    }),
    [t]
  );

  const folaActionMode = React.useMemo(
    () =>
      isFolaConversation
        ? deriveFolaActionMode(items, effectiveFolaPrompt, [folaUiCopy.actions.yes])
        : "default",
    [effectiveFolaPrompt, folaUiCopy.actions.yes, isFolaConversation, items]
  );
  const folaOptionChoices = React.useMemo(
    () => (isFolaConversation ? deriveFolaOptions(items, effectiveFolaPrompt, folaUiCopy) : []),
    [effectiveFolaPrompt, folaUiCopy, isFolaConversation, items]
  );

  React.useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const storageKey = getAccessibilityFolaStorageKey();
    const syncPrefs = () => setFolaPreferences(readAccessibilityFolaPrefs());
    const handleStorage = (event) => {
      if (event?.key && event.key !== storageKey) return;
      syncPrefs();
    };

    window.addEventListener("afrimed-accessibility-change", syncPrefs);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("afrimed-accessibility-change", syncPrefs);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  React.useEffect(() => {
    hiddenMessageIdsRef.current = new Set(hiddenMessageIds);
  }, [hiddenMessageIds]);

  React.useEffect(() => {
    const cachedState = readConversationUiState(currentUserId, conversationId);
    // Route entry must start clean. In particular, a validation error from a
    // previous thread cannot become an error in Fola or New message.
    setThreadError("");
    sendInFlightRef.current = false;
    if (conversationId === "fola") {
      setFolaResumeComplete(false);
      setGovernedFolaConversationId("");
    }
    setDraft(shouldAutoSubmitPharmacyPrompt ? "" : cachedState.draft);
    setSelectedOptions(cachedState.selectedOptions);
    setPendingAttachment(cachedState.pendingAttachment);
    setDeleteTarget(null);
    setPendingDeleteTargetMessageId(cachedState.deleteTargetMessageId);
    if (cachedSnapshot && !shouldTypePharmacyDirectOpening) {
      setItems(Array.isArray(cachedSnapshot.items) ? cachedSnapshot.items : []);
      setResolvedTitle(String(cachedSnapshot.resolvedTitle || initialTitle));
      setResolvedConversationId(cachedSnapshot.resolvedConversationId || null);
      setResolvedConversationDescription("");
      setHiddenMessageIds(
        Array.isArray(cachedSnapshot.hiddenMessageIds) ? cachedSnapshot.hiddenMessageIds : []
      );
      setLoading(false);
      return;
    }

    setItems([]);
    setResolvedTitle(initialTitle);
    setResolvedConversationId(conversationId === "fola" ? "fola" : null);
    setResolvedConversationDescription("");
    setHiddenMessageIds([]);
    setHasOlderMessages(false);
    liveMessageOffsetRef.current = 0;
    setLoading(conversationId !== "fola");
  }, [cachedSnapshot, conversationId, currentUserId, initialTitle, shouldTypePharmacyDirectOpening]);

  React.useEffect(() => {
    writeConversationUiState(currentUserId, conversationId, {
      draft,
      selectedOptions,
      pendingAttachment,
      deleteTargetMessageId:
        deleteTarget?.messageId || pendingDeleteTargetMessageId,
    });
  }, [
    conversationId,
    currentUserId,
    deleteTarget,
    draft,
    pendingAttachment,
    pendingDeleteTargetMessageId,
    selectedOptions,
  ]);

  React.useEffect(() => {
    if (isFolaConversation || !resolvedConversationId) return;

    writeConversationSnapshot(currentUserId, conversationId, {
      items,
      resolvedTitle,
      resolvedConversationId,
      hiddenMessageIds,
    });
  }, [
    conversationId,
    currentUserId,
    hiddenMessageIds,
    isFolaConversation,
    items,
    resolvedConversationId,
    resolvedTitle,
  ]);

  React.useEffect(() => {
    if (isFolaConversation || !resolvedConversationId) return;
    writeLastMessageThreadPath(currentUserId, `/messages/${resolvedConversationId}`);
  }, [currentUserId, isFolaConversation, resolvedConversationId]);

  React.useEffect(() => {
    const activeConversationId = String(
      isFolaConversation ? "fola" : resolvedConversationId || ""
    ).trim();

    window.dispatchEvent(
      new CustomEvent(ACTIVE_MESSAGE_THREAD_EVENT, {
        detail: {
          conversationId: activeConversationId,
        },
      })
    );

    return () => {
      window.dispatchEvent(
        new CustomEvent(ACTIVE_MESSAGE_THREAD_EVENT, {
          detail: {
            conversationId: "",
          },
        })
      );
    };
  }, [isFolaConversation, resolvedConversationId]);

  React.useEffect(() => {
    if (isFolaConversation || !resolvedConversationId || !currentUserId) return;
    if (lastOpenedConversationIdRef.current === resolvedConversationId) return;

    lastOpenedConversationIdRef.current = resolvedConversationId;
    void messagingService.markMessagesAsRead(resolvedConversationId);
  }, [currentUserId, isFolaConversation, resolvedConversationId]);

  const loadLiveThread = React.useCallback(
    async (liveConversationId, initialConversation = null, hiddenIdsOverride = null, { offset = 0, prepend = false } = {}) => {
      const loadSequence = ++threadLoadSequenceRef.current;
      const [conversation, messages] = await Promise.all([
        initialConversation
          ? Promise.resolve(initialConversation)
          : messagingService.getConversationById(liveConversationId),
        messagingService.getMessages(liveConversationId, 50, offset),
      ]);
      const participants = Array.isArray(conversation?.conversation_participants)
        ? conversation.conversation_participants
        : [];
      const nextItems = await mapLiveMessages(
        messages,
        currentUserId,
        participants,
        hiddenIdsOverride instanceof Set ? hiddenIdsOverride : hiddenMessageIdsRef.current,
        {
          locale: preferredLanguageCode,
          nowLabel: conversationCopy.now,
          deletedForMeText: conversationCopy.deletedForMe,
          deletedBySenderText: conversationCopy.deletedBySender,
        }
      );
      // A slower pre-send refresh must not overwrite a newer persisted reply.
      if (loadSequence !== threadLoadSequenceRef.current && !prepend) return nextItems;
      setThreadRefreshError("");
      if (prepend) {
        setItems((current) => {
          const known = new Set(current.map((item) => String(item?.id || "")));
          return [...nextItems.filter((item) => !known.has(String(item?.id || ""))), ...current];
        });
      } else {
        setItems(nextItems);
        liveMessageOffsetRef.current = Array.isArray(messages) ? messages.length : 0;
      }
      setHasOlderMessages(Array.isArray(messages) && messages.length === 50);
      setResolvedConversationDescription(String(conversation?.description || "").trim());
      if (conversation?.id) {
        setResolvedTitle(
          resolveLiveTitle(conversation, currentUserId, locationConversationTitle)
        );
      }

      const unreadCount = messagingService.calculateUnreadCount(
        messages,
        participants,
        currentUserId
      );

      if (unreadCount > 0) {
        await messagingService.markMessagesAsRead(liveConversationId);
      }

      return nextItems;
    },
    [
      conversationCopy.deletedBySender,
      conversationCopy.deletedForMe,
      conversationCopy.now,
      currentUserId,
      locationConversationTitle,
      preferredLanguageCode,
    ]
  );

  const loadOlderMessages = React.useCallback(async () => {
    if (isFolaConversation || !resolvedConversationId || loadingOlderMessages || !hasOlderMessages) return;
    timeline.capturePrependPosition();
    setLoadingOlderMessages(true);
    try {
      const nextItems = await loadLiveThread(
        resolvedConversationId,
        null,
        null,
        { offset: liveMessageOffsetRef.current, prepend: true },
      );
      liveMessageOffsetRef.current += nextItems.filter((item) => item?.messageId).length;
    } finally {
      setLoadingOlderMessages(false);
    }
  }, [hasOlderMessages, isFolaConversation, loadLiveThread, loadingOlderMessages, resolvedConversationId, timeline]);

  const handleTimelineScroll = React.useCallback((event) => {
    timeline.onScroll(event);
    if (event.currentTarget.scrollTop <= 48) void loadOlderMessages();
  }, [loadOlderMessages, timeline]);

  const loadSourceThreadContext = React.useCallback(async () => {
    if (!sourceConversationId) return null;

    try {
      const [conversation, messages] = await Promise.all([
        messagingService.getConversationById(sourceConversationId),
        messagingService.getMessages(sourceConversationId),
      ]);

      const nextContext = {
        conversationId: sourceConversationId,
        title:
          String(
            location.state?.returnToState?.conversationTitle ||
              conversation?.title ||
              locationConversationTitle ||
              ""
          ).trim() || conversationCopy.sourceThreadFallback,
        messages: Array.isArray(messages) ? messages : [],
      };

      setSourceThreadContext(nextContext);
      return nextContext;
    } catch {
      return null;
    }
  }, [
    conversationCopy.sourceThreadFallback,
    location.state?.returnToState?.conversationTitle,
    locationConversationTitle,
    sourceConversationId,
  ]);

  React.useEffect(() => {
    if (!isFolaConversation || currentRole !== "doctor" || !sourceConversationId) {
      setSourceThreadContext(null);
      return;
    }

    void loadSourceThreadContext();
  }, [currentRole, isFolaConversation, loadSourceThreadContext, sourceConversationId]);

  React.useEffect(() => {
    if (!toast?.message) return undefined;
    const timer = window.setTimeout(
      () => setToast({ message: "", actionLabel: "", onAction: null }),
      TOAST_DURATION_MS
    );
    return () => window.clearTimeout(timer);
  }, [toast]);

  React.useEffect(() => {
    let isMounted = true;

    async function hydrateConversation() {
      const cachedUiState = readConversationUiState(currentUserId, conversationId);
      const cachedThreadSnapshot = readConversationSnapshot(currentUserId, conversationId);
      setThreadError("");
      setDraft(cachedUiState.draft);
      setSelectedOptions(cachedUiState.selectedOptions);
      setThinking(false);
      setPendingAttachment(cachedUiState.pendingAttachment);
      setDeleteTarget(null);
      setPendingDeleteTargetMessageId(cachedUiState.deleteTargetMessageId);
      setResolvedTitle(String(cachedThreadSnapshot?.resolvedTitle || initialTitle));

    if (isFolaConversation) {
      setResolvedConversationId("fola");
      setHiddenMessageIds([]);
      setItems(
        shouldAutoSubmitPharmacyPrompt || shouldTypePharmacyDirectOpening
          ? []
          : folaRole === "patient" ? [] : readStoredFolaItems(currentUserId)
      );
      setDraft((current) =>
        shouldAutoSubmitPharmacyPrompt || shouldTypePharmacyDirectOpening
          ? ""
          : current || cachedUiState.draft || initialDraftFromState
      );

      if (folaRole === "patient" && !shouldAutoSubmitPharmacyPrompt && !shouldTypePharmacyDirectOpening) {
        // Patient Fola is server-owned. Rehydrate the durable conversation and
        // any active booking workflow instead of treating every route mount as
        // a new chat. `resume` is a protocol action, not an empty message turn.
        setLoading(true);
        try {
          await runGovernedPatientFolaTurn({ kind: "resume" });
        } catch (error) {
          if (!isMounted) return;
          setThreadError(error?.message || folaUiCopy.states.error);
          setFolaResumeComplete(true);
        } finally {
          if (isMounted) setLoading(false);
        }
        return;
      }

      setFolaResumeComplete(true);
      setLoading(false);
      return;
    }

      if (!cachedThreadSnapshot) {
        setLoading(true);
      }

      try {
        let conversation = null;

        const participantUserId = String(
          location.state?.participantUserId ||
            location.state?.doctorId ||
            location.state?.patientId ||
            ""
        ).trim();

        if (!blocksDirectFallback && location.state?.appointmentId && participantUserId && participantUserId !== currentUserId) {
          // Appointment routes carry a counterpart Auth user ID, not a thread ID.
          // Resolve the exact pair before interpreting the URL UUID as a conversation.
          conversation = await messagingService.getOrCreateDirectConversation({
            otherUserId: participantUserId,
            title: String(location.state?.conversationTitle || "").trim(),
          });
        } else if (isUuid(conversationId)) {
          conversation = await messagingService.getConversationById(conversationId);

          if (
            !conversation?.id &&
            !blocksDirectFallback &&
            participantUserId &&
            participantUserId !== currentUserId
          ) {
            conversation = await messagingService.getOrCreateDirectConversation({
              otherUserId: participantUserId,
              title: String(location.state?.conversationTitle || "").trim(),
            });
          } else if (
            !conversation?.id &&
            !blocksDirectFallback &&
            conversationId !== currentUserId
          ) {
            conversation = await messagingService.getOrCreateDirectConversation({
              otherUserId: conversationId,
              title: String(location.state?.conversationTitle || "").trim(),
            });
          }
        } else if (!blocksDirectFallback && participantUserId && participantUserId !== currentUserId) {
          conversation = await messagingService.getOrCreateDirectConversation({
            otherUserId: participantUserId,
            title: String(location.state?.conversationTitle || "").trim(),
          });
        } else if (!blocksDirectFallback) {
          conversation = await messagingService.getOrCreateDirectConversation({
            otherUserId: String(conversationId || "").trim(),
            title: String(location.state?.conversationTitle || "").trim(),
          });
        }

        if (!isMounted) return;
        if (!conversation?.id) {
          setResolvedConversationId(null);
          setResolvedConversationDescription("");
          setItems([]);
          setThreadError(conversationCopy.errors.resolve);
          return;
        }

        if (
          currentRole === "patient" &&
          !(await isValidPatientFulfillmentConversation({
            conversation,
            currentUserId,
          }))
        ) {
          setResolvedConversationId(null);
          setResolvedConversationDescription("");
          setItems([]);
          setThreadError(conversationCopy.errors.resolve);
          return;
        }

        setResolvedConversationId(conversation.id);
        setResolvedConversationDescription(String(conversation?.description || "").trim());
        const storedHiddenMessageIds = readHiddenMessageIds(
          currentUserId,
          conversation.id
        );
        setHiddenMessageIds(storedHiddenMessageIds);
        setResolvedTitle(
          resolveLiveTitle(
            conversation,
            currentUserId,
            locationConversationTitle
          )
        );

        await loadLiveThread(
          conversation.id,
          conversation,
          new Set(storedHiddenMessageIds)
        );
      } catch (error) {
        if (!isMounted) return;
        setResolvedConversationId(null);
        setResolvedConversationDescription("");
        setItems([]);
        setThreadError(error?.message || conversationCopy.errors.load);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    hydrateConversation();
    return () => {
      isMounted = false;
    };
  }, [
    conversationId,
    currentUserId,
    initialDraftFromState,
    initialTitle,
    isFolaConversation,
    loadLiveThread,
    location.state,
    locationConversationTitle,
    shouldAutoSubmitPharmacyPrompt,
    shouldTypePharmacyDirectOpening,
  ]);

  React.useEffect(() => {
    if (isFolaConversation || !resolvedConversationId) return undefined;

    let refreshTimer = 0;
    const refreshThread = () => {
      window.clearTimeout(refreshTimer);
      refreshTimer = window.setTimeout(() => {
        void loadLiveThread(resolvedConversationId).catch((error) => {
          setThreadRefreshError(error?.message || "Messages could not refresh. Check your connection and retry.");
        });
      }, 80);
    };

    const channel = messagingService.subscribeToConversation(
      resolvedConversationId,
      refreshThread
    );

    const recoverVisibleThread = () => {
      if (document.visibilityState !== "hidden") refreshThread();
    };
    window.addEventListener("focus", recoverVisibleThread);
    window.addEventListener("online", recoverVisibleThread);
    document.addEventListener("visibilitychange", recoverVisibleThread);
    return () => {
      window.clearTimeout(refreshTimer);
      window.removeEventListener("focus", recoverVisibleThread);
      window.removeEventListener("online", recoverVisibleThread);
      document.removeEventListener("visibilitychange", recoverVisibleThread);
      messagingService.unsubscribe(channel);
    };
  }, [isFolaConversation, loadLiveThread, resolvedConversationId]);

  React.useEffect(() => {
    if (!pendingDeleteTargetMessageId || deleteTarget) return;

    const matchingItem = items.find(
      (item) =>
        item?.canDelete &&
        normalizeDeleteTargetMessageId(item?.messageId) === pendingDeleteTargetMessageId
    );

    if (!matchingItem) return;
    setDeleteTarget(matchingItem);
    setPendingDeleteTargetMessageId("");
  }, [deleteTarget, items, pendingDeleteTargetMessageId]);

  React.useEffect(() => {
    if (!isFolaConversation || folaRole === "patient") return;
    writeStoredFolaItems(currentUserId, items);
  }, [currentUserId, folaRole, isFolaConversation, items]);

  React.useEffect(() => {
    if (!isFolaConversation || loading) return;
    if (folaRole === "patient" && !folaResumeComplete) return;
    if (shouldAutoSubmitPharmacyPrompt) return;
    if (shouldTypePharmacyDirectOpening) return;
    if (items.length > 0) return;

    setItems(
      getInitialFolaItems({
        role: folaRole,
        locale: preferredLanguageCode,
        preferences: folaPreferences,
        sourceContext: folaSourceContext,
      })
    );
  }, [
    folaPreferences,
    folaRole,
    folaSourceContext,
    isFolaConversation,
    items.length,
    loading,
    preferredLanguageCode,
    shouldAutoSubmitPharmacyPrompt,
    shouldTypePharmacyDirectOpening,
    folaResumeComplete,
  ]);

  React.useEffect(() => {
    if (!isFolaConversation || loading) return;
    if (folaRole !== "patient") return;
    if (items.length === 0 || items.length > 2) return;
    if (activeFolaPrompt?.promptKey !== "patient-starter") return;

    setItems(
      getInitialFolaItems({
        role: "patient",
        locale: preferredLanguageCode,
        preferences: folaPreferences,
        sourceContext: folaSourceContext,
      })
    );
  }, [
    activeFolaPrompt?.promptKey,
    folaPreferences,
    folaRole,
    folaSourceContext,
    isFolaConversation,
    items.length,
    loading,
    preferredLanguageCode,
  ]);

  React.useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const { body, documentElement } = document;
    const previousBodyOverflow = body.style.overflow;
    const previousHtmlOverflow = documentElement.style.overflow;
    const previousBodyOverscroll = body.style.overscrollBehavior;
    const previousHtmlOverscroll = documentElement.style.overscrollBehavior;

    body.style.overflow = "hidden";
    documentElement.style.overflow = "hidden";
    body.style.overscrollBehavior = "none";
    documentElement.style.overscrollBehavior = "none";

    return () => {
      body.style.overflow = previousBodyOverflow;
      documentElement.style.overflow = previousHtmlOverflow;
      body.style.overscrollBehavior = previousBodyOverscroll;
      documentElement.style.overscrollBehavior = previousHtmlOverscroll;
    };
  }, []);

  const appendFulfillmentFolaReply = React.useCallback(
    async ({ liveConversationId, text }) => {
      const prescriptionId = parseFulfillmentPrescriptionId(resolvedConversationDescription);
      if (!prescriptionId) return;

      const snapshot = await fetchFulfillmentReplySnapshot({
        supabase,
        prescriptionId,
      });
      const intent = classifyFulfillmentMessageIntent(text);
      const reply = buildFulfillmentFolaReply({ intent, snapshot, t });
      const replyText = String(reply?.text || "").trim();
      if (!replyText) return;

      const { error } = await supabase.functions.invoke("append-fola-fulfillment-reply", {
        body: {
          conversation_id: liveConversationId,
          prescription_id: prescriptionId,
          content: replyText,
          kind: String(reply?.kind || "fulfillment_help").trim(),
          intent,
          fulfillment_status: String(snapshot?.status || "").trim(),
          fulfillment_mode: String(snapshot?.mode || "").trim(),
          fulfillment_updated_at: String(snapshot?.updatedAt || "").trim(),
          pharmacy_name: String(snapshot?.pharmacyName || "").trim(),
        },
      });

      if (error) throw error;
      await loadLiveThread(liveConversationId);
    },
    [loadLiveThread, resolvedConversationDescription, t]
  );

  async function revealLocalFolaFollowUps(followUps = []) {
    const sequence = localRevealSequenceRef.current + 1;
    localRevealSequenceRef.current = sequence;
    setFolaRevealing(true);

    try {
      for (const [index, item] of followUps.entries()) {
        if (localRevealSequenceRef.current !== sequence) return;

        const finalText = String(item?.text || "");
        const canRevealText =
          item?.type === "text" &&
          item?.side !== "right" &&
          finalText.length > LOCAL_FOLA_REVEAL_CHARS_PER_TICK;

        if (!canRevealText) {
          setItems((current) => [...current, item]);
          continue;
        }

        const revealId = String(item?.id || `local-reveal-${Date.now()}-${index}`);
        const firstSlice = finalText.slice(0, LOCAL_FOLA_REVEAL_CHARS_PER_TICK);
        setItems((current) => [
          ...current,
          {
            ...item,
            id: revealId,
            text: firstSlice,
          },
        ]);

        for (
          let visibleLength = firstSlice.length;
          visibleLength < finalText.length;
          visibleLength += LOCAL_FOLA_REVEAL_CHARS_PER_TICK
        ) {
          if (localRevealSequenceRef.current !== sequence) return;
          await waitForLocalFolaRevealTick();
          const nextText = finalText.slice(
            0,
            Math.min(finalText.length, visibleLength + LOCAL_FOLA_REVEAL_CHARS_PER_TICK)
          );
          setItems((current) =>
            current.map((currentItem) =>
              currentItem?.id === revealId
                ? {
                    ...currentItem,
                    text: nextText,
                  }
                : currentItem
            )
          );
        }
      }
    } finally {
      if (localRevealSequenceRef.current === sequence) {
        setFolaRevealing(false);
      }
    }
  }

  React.useEffect(() => {
    if (!shouldTypePharmacyDirectOpening) return;
    if (!isFolaConversation || folaRole !== "pharmacy") return;
    if (loading || thinking || folaRevealing) return;
    if (items.length > 0) return;

    const openingKey = `${currentUserId || "guest"}:${location.pathname}`;
    if (typedPharmacyOpeningRef.current === openingKey) return;
    typedPharmacyOpeningRef.current = openingKey;

    async function revealPharmacyOpening() {
      setDraft("");
      setSelectedOptions([]);
      setThinking(true);
      await waitForLocalFolaReply();
      setThinking(false);

      const initialPharmacyItems = getInitialFolaItems({
        role: "pharmacy",
        locale: preferredLanguageCode,
        preferences: folaPreferences,
        sourceContext: folaSourceContext,
      });
      const welcomeItem = initialPharmacyItems.find(
        (item) => item?.type === "text" && item?.side !== "right"
      );
      const starterItem = initialPharmacyItems.find((item) => item?.type === "fola-prompt");
      if (welcomeItem) {
        await revealLocalFolaFollowUps([welcomeItem]);
      }
      if (starterItem) {
        setItems((current) =>
          current.some((item) => item?.type === "fola-prompt" && item?.promptKey === starterItem.promptKey)
            ? current
            : [...current, starterItem]
        );
      }
    }

    void revealPharmacyOpening();
  }, [
    currentUserId,
    folaPreferences,
    folaRevealing,
    folaRole,
    folaSourceContext,
    isFolaConversation,
    items.length,
    loading,
    location.pathname,
    preferredLanguageCode,
    shouldTypePharmacyDirectOpening,
    thinking,
  ]);

  React.useEffect(() => {
    if (!isFolaConversation || folaRole !== "pharmacy") return;
    if (!promptKeyFromQuery || !initialDraftFromState) return;
    if (loading || thinking || folaRevealing) return;

    const autoRunKey = `${promptKeyFromQuery}:${initialDraftFromState}`;
    if (autoSubmittedPharmacyPromptRef.current === autoRunKey) return;
    autoSubmittedPharmacyPromptRef.current = autoRunKey;

    const hasPromptResponse = items.some((item) => {
      if (item?.type !== "text" || item?.side === "right") return false;
      const normalizedText = String(item?.text || "").trim().toLowerCase();
      return (
        normalizedText.includes("pharmacy dashboard overview") ||
        normalizedText.includes("pharmacy payments overview") ||
        normalizedText.includes("add stock item guidance") ||
        normalizedText.includes("prescription order guidance") ||
        normalizedText.includes("pharmacy roles") ||
        normalizedText.includes("staff invite") ||
        normalizedText.includes("csv import") ||
        normalizedText.includes("fulfilment handoff")
      );
    });
    if (hasPromptResponse) {
      setDraft("");
      return;
    }

    const localResolution = resolveFolaFreeformInput({
      text: initialDraftFromState,
      role: "pharmacy",
      locale: preferredLanguageCode,
      preferences: folaPreferences,
      sourceContext: folaSourceContext,
    });

    setDraft("");
    setSelectedOptions([]);

    async function revealAutoReply() {
      setThinking(true);
      await waitForLocalFolaReply();
      setThinking(false);
      const initialPharmacyItems = getInitialFolaItems({
        role: "pharmacy",
        locale: preferredLanguageCode,
        preferences: folaPreferences,
        sourceContext: folaSourceContext,
      });
      const welcomeItem = initialPharmacyItems.find(
        (item) => item?.type === "text" && item?.side !== "right"
      );
      const starterItem = initialPharmacyItems.find((item) => item?.type === "fola-prompt");
      if (welcomeItem) {
        await revealLocalFolaFollowUps([welcomeItem]);
      }
      if (starterItem) {
        setItems((current) =>
          current.some((item) => item?.type === "fola-prompt" && item?.promptKey === starterItem.promptKey)
            ? current
            : [...current, starterItem]
        );
      }
      await waitForPharmacyGreetingPause();
      setThinking(true);
      await waitForLocalFolaReply();
      setThinking(false);
      await revealLocalFolaFollowUps(
        Array.isArray(localResolution?.followUps) ? localResolution.followUps : []
      );
    }

    void revealAutoReply();
  }, [
    conversationCopy.now,
    folaPreferences,
    folaRevealing,
    folaRole,
    folaSourceContext,
    initialDraftFromState,
    isFolaConversation,
    items,
    loading,
    preferredLanguageCode,
    promptKeyFromQuery,
    thinking,
  ]);

  async function runGovernedPatientFolaTurn(message) {
    const turnId = `governed-fola-${Date.now()}`;
    let streamedReply = "";
    setThinking(true);
    setGovernedFolaStage("Thinking…");
    try {
      await streamFolaOrchestratorTurn({
        ...(message && typeof message === "object" ? message : { kind: "turn", message }),
        conversation_id: governedFolaConversationId || undefined,
        surface: "messages_fola",
        route: location.pathname,
        locale: preferredLanguageCode,
      }, ({ type, payload }) => {
        if (type === "turn.accepted") {
          setGovernedFolaConversationId(String(payload?.conversation_id || ""));
          return;
        }
        if (type === "conversation.resumed") {
          const restoredTurns = (Array.isArray(payload?.turns) ? payload.turns : [])
            .map((turn, index) => {
              const actor = String(turn?.actor || "").trim();
              const responsePayload = turn?.response_payload && typeof turn.response_payload === "object"
                ? turn.response_payload
                : {};
              const turnText = actor === "user"
                ? String(turn?.request_text || "").trim()
                : String(responsePayload?.text || "").trim();
              if (!turnText) return null;
              return {
                id: `fola-resumed-${String(turn?.created_at || index)}-${index}`,
                type: "text",
                side: actor === "user" ? "right" : "left",
                text: turnText,
                time: turn?.created_at ? formatClock(turn.created_at, preferredLanguageCode, conversationCopy.now) : conversationCopy.now,
                ...(actor === "fola" ? { outputType: "support" } : {}),
              };
            })
            .filter(Boolean);

          const booking = payload?.booking_context;
          const checkout = payload?.checkout;
          const resumeItems = [...restoredTurns];

          if (booking && typeof booking === "object") {
            const clinician = booking?.clinician || {};
            const service = booking?.selected_service || {};
            const serviceName = String(booking?.service_name || service?.name || "").trim();
            const date = String(booking?.requested_date || "").trim();
            const selectedSlot = String(booking?.selected_slot || "").trim();

            if (checkout?.route) {
              resumeItems.push({
                id: `booking-resume-${Date.now()}`,
                type: "text",
                side: "left",
                text: `${String(clinician?.name || "Your clinician")} booking${serviceName ? ` for ${serviceName}` : ""}${selectedSlot ? ` at ${selectedSlot}` : ""} is still awaiting payment.`,
                time: conversationCopy.now,
                outputType: "support",
                action: { id: "resume-booking-checkout", label: "Continue to checkout", route: checkout.route, state: checkout.state || {} },
              });
            } else if (booking?.unresolved_service_requirement) {
              resumeItems.push({
                id: `booking-services-resume-${Date.now()}`,
                type: "fola-booking-services",
                intro: "Choose a service.",
                services: (Array.isArray(booking?.services) ? booking.services : []).map((entry) => ({ id: String(entry?.id || ""), name: String(entry?.name || ""), durationMinutes: Number(entry?.duration_minutes || 0), price: Number(entry?.price || 0), currency: String(entry?.currency || "ZAR") })).filter((entry) => entry.id && entry.name),
                action: { clinicianId: clinician?.id, clinicianName: clinician?.name },
                time: conversationCopy.now,
              });
            } else if (Array.isArray(booking?.candidate_slots) && booking.candidate_slots.length) {
              resumeItems.push({
                id: `booking-options-resume-${Date.now()}`,
                type: "fola-booking-options",
                intro: `Choose an available${booking?.requested_daypart ? ` ${String(booking.requested_daypart)}` : ""} time.`,
                slots: booking.candidate_slots,
                dateLabel: formatFolaBookingDate(date),
                action: { clinicianId: clinician?.id, clinicianName: clinician?.name, serviceId: booking?.service_id || service?.id, date, durationMinutes: booking?.service_duration_minutes || service?.duration_minutes },
                time: conversationCopy.now,
              });
            }
          }

          setItems(resumeItems);
          setFolaResumeComplete(true);
          return;
        }
        if (type === "context.resolved") {
          setGovernedFolaStage("Checking your booking context…");
          return;
        }
        if (type === "tool.started") {
          setGovernedFolaStage(String(payload?.label || "Working…"));
          return;
        }
        if (type === "tool.completed") {
          setGovernedFolaStage("");
          return;
        }
        if (type === "response.delta") {
          streamedReply += String(payload?.delta || "");
          setItems((current) => {
            const existing = current.find((item) => item?.id === turnId);
            const reply = { id: turnId, type: "text", side: "left", text: streamedReply, time: conversationCopy.now, outputType: "support" };
            return existing ? current.map((item) => item?.id === turnId ? reply : item) : [...current, reply];
          });
          return;
        }
        if (type === "action.proposed" && payload?.kind === "availability") {
          setItems((current) => [...current, {
            id: `booking-options-${Date.now()}`,
            type: "fola-booking-options",
            intro: String(payload?.intro || "Choose an available time."),
            slots: Array.isArray(payload?.slots) ? payload.slots : [],
            dateLabel: String(payload?.date_label || ""),
            action: { clinicianId: payload?.clinician_id, clinicianName: payload?.clinician_name, serviceId: payload?.service_id, date: payload?.date, durationMinutes: payload?.duration_minutes },
            time: conversationCopy.now,
          }]);
          return;
        }
        if (type === "action.proposed" && payload?.kind === "service_selection") {
          setItems((current) => [...current, {
            id: `booking-services-${Date.now()}`,
            type: "fola-booking-services",
            intro: String(payload?.intro || "Choose a service."),
            services: (Array.isArray(payload?.services) ? payload.services : []).map((service) => ({ id: String(service?.id || ""), name: String(service?.name || ""), durationMinutes: Number(service?.duration_minutes || 0), price: Number(service?.price || 0), currency: String(service?.currency || "ZAR") })).filter((service) => service.id && service.name),
            action: { clinicianId: payload?.clinician_id, clinicianName: payload?.clinician_name },
            time: conversationCopy.now,
          }]);
          return;
        }
        if (type === "action.proposed" && payload?.kind === "booking_decision") {
          setItems((current) => [...current, {
            id: `booking-decision-${Date.now()}`,
            type: "fola-booking-decision",
            decision: String(payload?.decision || ""),
            intro: String(payload?.intro || "Choose an option."),
            options: Array.isArray(payload?.options) ? payload.options : [],
            time: conversationCopy.now,
          }]);
          return;
        }
        if (type === "action.confirmation_required") {
          setItems((current) => [...current, {
            id: `booking-confirmation-${Date.now()}`,
            type: "fola-booking-confirmation",
            intentId: String(payload?.intent_id || ""),
            summary: String(payload?.summary || "Please confirm this booking intent before checkout."),
            time: conversationCopy.now,
          }]);
          return;
        }
        if (type === "checkout.handoff_required") {
          setGovernedFolaStage("Opening secure checkout…");
          const route = String(payload?.checkout?.route || "");
          if (route) navigate(route, { state: payload.checkout.state || {} });
          return;
        }
        if (type === "turn.failed") {
          throw new Error(String(payload?.message || "Fola could not complete that request."));
        }
      });
    } finally {
      setThinking(false);
      setGovernedFolaStage("");
    }
  }

  async function handleSendText() {
    // State updates are asynchronous; this guard closes the double-click window
    // before the disabled visual state reaches the button.
    if (sendInFlightRef.current) return;
    const selectedChoice =
      folaOptionChoices.find((choice) => choice?.id === selectedOptions[0]) || null;
    const composedDraft =
      draft.trim() ||
      String(selectedChoice?.label || selectedOptions.join(", ") || "").trim();

    if (thinking || folaRevealing || loading) return;
    const validationError = sendValidationError({
      intent: "send",
      draft: composedDraft,
      hasAttachment: Boolean(pendingAttachment?.file),
    });
    if (validationError) {
      setThreadError(validationError);
      return;
    }
    setThreadError((current) => current === "message_required" ? "" : current);

    if (
      isFolaConversation &&
      !draft.trim() &&
      !pendingAttachment?.file &&
      selectedChoice
    ) {
      handleFolaChoice(selectedChoice);
      return;
    }

    sendInFlightRef.current = true;

    if (isFolaConversation) {
      const appendedUserMessage = {
        id: `text-${Date.now()}`,
        type: "text",
        side: "right",
        text: composedDraft,
        time: conversationCopy.now,
      };

      setItems((current) => [
        ...current,
        appendedUserMessage,
      ]);
      setDraft("");
      setSelectedOptions([]);

      if (folaRole === "patient") {
        try {
          await runGovernedPatientFolaTurn(composedDraft);
          clearRichMessageDraft(currentDraftStorageKey);
        } catch (error) {
          setItems((current) => [...current, {
            id: `governed-fola-error-${Date.now()}`,
            type: "text",
            side: "left",
            text: error?.message || folaUiCopy.states.error,
            time: conversationCopy.now,
            outputType: "support",
          }]);
        }
        sendInFlightRef.current = false;
        return;
      }

      const localResolution = resolveFolaFreeformInput({
        text: composedDraft,
        role: folaRole,
        locale: preferredLanguageCode,
        preferences: folaPreferences,
        sourceContext: folaSourceContext,
      });

      if (localResolution?.skipRemote) {
        setThinking(true);
        await waitForLocalFolaReply();
        setThinking(false);
        await revealLocalFolaFollowUps(
          Array.isArray(localResolution.followUps)
            ? localResolution.followUps
            : []
        );
        return;
      }

      setThinking(true);

      try {
        const { data: session } = await supabase.auth.getUser();
        const { data, error } = await supabase.functions.invoke("fola", {
          body: {
            message: composedDraft,
            email: session?.user?.email || undefined,
            user_id: session?.user?.id || currentUserId || undefined,
            role: currentRole || undefined,
            route: location.pathname,
            section: isFolaConversation ? "messages_fola" : "messages",
            locale: preferredLanguageCode,
            preferences: folaPreferences,
            history: buildFolaHistory(items, composedDraft),
            source_context: folaSourceContext,
          },
        });

        const reply =
          data?.reply ||
          (error
            ? folaUiCopy.states.error
            : folaUiCopy.states.imageReviewLimited);
        const actionChoices = Array.isArray(data?.actions)
          ? data.actions
              .filter((action) => action?.id && action?.label && action?.route)
              .slice(0, 5)
              .map((action) => ({
                id: String(action.id),
                label: String(action.label),
                variant: action.variant || "ghost",
                route: String(action.route),
              }))
          : [];

        setItems((current) => [
          ...current,
          {
            id: `reply-${Date.now()}`,
            type: "text",
            side: "left",
            text: reply,
            time: conversationCopy.now,
            outputType: "support",
            sourceContext: folaSourceContext,
          },
          ...(actionChoices.length > 0
            ? [
                {
                  id: `appointment-actions-${Date.now() + 1}`,
                  type: "fola-prompt",
                  side: "left",
                  time: conversationCopy.now,
                  promptKey: "appointment-help-actions",
                  title: "",
                  intro: "",
                  layout: "grid",
                  choices: actionChoices,
                  outputType: "support",
                  sourceContext: folaSourceContext,
                },
              ]
            : []),
        ]);
      } catch {
        setItems((current) => [
          ...current,
          {
            id: `reply-${Date.now()}`,
            type: "text",
            side: "left",
            text: folaUiCopy.states.error,
            time: conversationCopy.now,
            outputType: "support",
            sourceContext: folaSourceContext,
          },
        ]);
      } finally {
        setThinking(false);
      }

      sendInFlightRef.current = false;
      return;
    }

    if (!resolvedConversationId) { sendInFlightRef.current = false; return; }

    const attachmentToSend = pendingAttachment;
    const optimisticMessage =
      composedDraft && !attachmentToSend?.file
        ? {
            id: `optimistic-${Date.now()}`,
            type: "text",
            side: "right",
            text: composedDraft,
            time: conversationCopy.now,
            sendState: "sending",
          }
        : null;

    try {
      setThinking(true);
      setDraft("");
      setSelectedOptions([]);
      if (optimisticMessage) {
        setItems((current) => [...current, optimisticMessage]);
      }
      const message = await messagingService.sendMessage(
        resolvedConversationId,
        composedDraft || "Shared an image",
        attachmentToSend?.file ? "image" : "text"
      );
      if (attachmentToSend?.file) {
        await messagingService.uploadAttachment(attachmentToSend.file, message.id);
      }
      // Keep the first useful content on screen. Realtime/paged loading will
      // reconcile later; a successful insert is enough to advance this item.
      if (optimisticMessage) {
        setItems((current) => current.map((item) => item.id === optimisticMessage.id
          ? { ...item, id: message.id || item.id, sendState: "sent" }
          : item));
      }
      const shouldAppendFulfillmentReply =
        currentRole !== "doctor" &&
        isPersistedFulfillmentConversation &&
        Boolean(composedDraft) &&
        !attachmentToSend?.file;

      if (shouldAppendFulfillmentReply) {
        try {
          await appendFulfillmentFolaReply({
            liveConversationId: resolvedConversationId,
            text: composedDraft,
          });
        } catch (replyError) {
          console.warn("Could not append Fola fulfillment reply:", replyError);
        }
      }

      releaseAttachmentPreview(attachmentToSend);
      setPendingAttachment(null);
      clearConversationUiState(currentUserId, conversationId);
      clearConversationSnapshot(currentUserId, conversationId);
      clearRichMessageDraft(currentDraftStorageKey);
    } catch (error) {
      if (optimisticMessage) {
        setItems((current) => current.filter((item) => item.id !== optimisticMessage.id));
      }
      setThreadError(error?.message || conversationCopy.errors.send);
      setDraft(composedDraft);
    } finally {
      setThinking(false);
      sendInFlightRef.current = false;
    }
  }

  function openFolaForCurrentThread() {
    navigate(messageEntryRoute(messagesBasePath, "fola"), {
      ...(isFolaConversation ? { replace: true } : {}),
      state: {
        ...messageEntryState("fola"),
        ...(!isFolaConversation ? {
          returnTo: location.pathname,
          returnToState: {
            ...(location.state || {}),
            conversationTitle: resolvedTitle,
          },
        } : {}),
      },
    });
  }

  function openNewMessageComposer() {
    setThreadError("");
    clearLastMessageThreadPath(currentUserId);
    navigate(messageEntryRoute(messagesBasePath, "compose"), { state: messageEntryState("compose") });
  }

  function handleAttachClick() {
    if (thinking || folaRevealing || loading) return;
    fileInputRef.current?.click();
  }

  function handleToggleOption(option) {
    setSelectedOptions((current) =>
      current.includes(option) ? [] : [option]
    );
  }

  async function handleFolaChoice(choice) {
    const choiceId = String(choice?.id || "").trim();
    if (!choiceId) return;
    if (thinking || folaRevealing || loading) return;

    if (choiceId === "fulfillment-pay-now" && choice?.route) {
      navigate(String(choice.route));
      return;
    }

    if (folaRole === "patient" && choiceId === "resume-booking-checkout" && choice?.route) {
      navigate(String(choice.route), { state: choice?.state || {} });
      return;
    }

    if (folaRole === "patient" && choiceId === "select-booking-slot") {
      const booking = choice?.action || {};
      const time = String(choice?.slot || "").trim();
      if (!time) return;
      const message = `I choose ${time}.`;
      setItems((current) => [...current, { id: `booking-slot-${Date.now()}`, type: "text", side: "right", text: message, time: conversationCopy.now }]);
      try {
        await runGovernedPatientFolaTurn({
          kind: "select_slot",
          clinician_id: String(booking?.clinicianId || ""),
          clinician_name: String(booking?.clinicianName || ""),
          date: String(booking?.date || ""),
          selected_time: time,
          service_id: String(booking?.serviceId || ""),
        });
      } catch (error) {
        setThreadError(error?.message || folaUiCopy.states.error);
      }
      return;
    }

    if (folaRole === "patient" && choiceId === "select-booking-decision") {
      const value = String(choice?.value || "").trim();
      if (!value) return;
      setItems((current) => [...current, { id: `booking-decision-choice-${Date.now()}`, type: "text", side: "right", text: value, time: conversationCopy.now }]);
      try { await runGovernedPatientFolaTurn({ kind: "select_booking_decision", decision: String(choice?.decision || ""), value }); } catch (error) { setThreadError(error?.message || folaUiCopy.states.error); }
      return;
    }

    if (folaRole === "patient" && choiceId === "select-booking-service") {
      const service = choice?.service || {};
      const message = String(service?.name || "").trim();
      if (!message) return;
      setItems((current) => [...current, { id: `booking-service-${Date.now()}`, type: "text", side: "right", text: message, time: conversationCopy.now }]);
      try { await runGovernedPatientFolaTurn(message); } catch (error) { setThreadError(error?.message || folaUiCopy.states.error); }
      return;
    }

    if (folaRole === "patient" && choiceId === "confirm-booking-intent") {
      const intentId = String(choice?.intentId || "").trim();
      if (!intentId) return;
      try {
        await runGovernedPatientFolaTurn({ kind: "confirm_booking_intent", intent_id: intentId });
      } catch (error) {
        setThreadError(error?.message || folaUiCopy.states.error);
      }
      return;
    }

    if (choiceId === "doctor-use-draft-reply") {
      const latestReplyCard = [...items]
        .reverse()
        .find((item) => item?.type === "fola-reply-card" && String(item?.body || "").trim());
      if (latestReplyCard?.body) {
        setDraft(latestReplyCard.body);
      }
      return;
    }

    let resolved = resolveFolaSelection({
      choiceId,
      role: folaRole,
      locale: preferredLanguageCode,
      preferences: folaPreferences,
      sourceContext: folaSourceContext,
    });
    if (!resolved && choice?.route) {
      resolved = {
        userText: String(choice?.label || "").trim(),
        route: String(choice.route),
      };
    }

    if (choiceId === "legacy-yes") {
      navigate("/appointments/hospitals");
      return;
    }

    if (choiceId === "legacy-no") {
      setItems((current) => [
        ...current,
        {
          id: `legacy-reply-${Date.now()}`,
          type: "text",
          side: "right",
          text: folaUiCopy.actions.no,
          time: conversationCopy.now,
        },
      ]);
      setSelectedOptions([]);
      return;
    }

    if (!resolved) return;

    if (
      currentRole === "doctor" &&
      [
        "doctor-summarize-thread",
        "doctor-draft-soap",
        "doctor-draft-reply",
      ].includes(choiceId)
    ) {
      const context = sourceThreadContext || (await loadSourceThreadContext());
      const copy = getFolaUiCopy(preferredLanguageCode);

      if (!context?.messages?.length) {
        setItems((current) => [
          ...current,
          {
            id: `choice-${Date.now()}`,
            type: "text",
            side: "right",
            text: resolved.userText,
            time: conversationCopy.now,
          },
          {
            id: `doctor-lane-${Date.now() + 1}`,
            type: "text",
            side: "left",
            text: copy.states.doctorNeedsThread,
            time: conversationCopy.now,
            outputType: "support",
            sourceContext: folaSourceContext,
          },
        ]);
        return;
      }

      let followUpCard = null;
      if (choiceId === "doctor-summarize-thread") {
        followUpCard = buildDoctorThreadSummaryCard({
          copy,
          threadTitle: context.title,
          messages: context.messages,
          currentUserId,
          nowLabel: conversationCopy.now,
          sourceContext: {
            ...folaSourceContext,
            kind: "message_thread",
            conversationId: context.conversationId || sourceConversationId || null,
          },
        });
      } else if (choiceId === "doctor-draft-soap") {
        followUpCard = buildDoctorSoapCard({
          copy,
          threadTitle: context.title,
          messages: context.messages,
          currentUserId,
          nowLabel: conversationCopy.now,
          sourceContext: {
            ...folaSourceContext,
            kind: "message_thread",
            conversationId: context.conversationId || sourceConversationId || null,
          },
        });
      } else if (choiceId === "doctor-draft-reply") {
        followUpCard = buildDoctorReplyCard({
          copy,
          threadTitle: context.title,
          messages: context.messages,
          currentUserId,
          nowLabel: conversationCopy.now,
          sourceContext: {
            ...folaSourceContext,
            kind: "message_thread",
            conversationId: context.conversationId || sourceConversationId || null,
          },
        });
      }

      setItems((current) => [
        ...current,
        {
          id: `choice-${Date.now()}`,
          type: "text",
          side: "right",
          text: resolved.userText,
          time: conversationCopy.now,
        },
        ...(followUpCard ? [followUpCard] : []),
      ]);
      setDraft("");
      setSelectedOptions([]);
      return;
    }

    const resolvedFollowUps = Array.isArray(resolved.followUps) ? resolved.followUps : [];

    if (folaRole === "pharmacy" && resolvedFollowUps.length > 0) {
      setItems((current) => {
        const nextItems = [...current];

        if (resolved.userText) {
          nextItems.push({
            id: `choice-${Date.now()}`,
            type: "text",
            side: "right",
            text: resolved.userText,
            time: conversationCopy.now,
          });
        }

        return nextItems;
      });

      setDraft("");
      setSelectedOptions([]);
      setThinking(true);
      await waitForLocalFolaReply();
      setThinking(false);
      await revealLocalFolaFollowUps(resolvedFollowUps);
      return;
    }

    setItems((current) => {
      const nextItems = [...current];

      if (resolved.userText) {
        nextItems.push({
          id: `choice-${Date.now()}`,
          type: "text",
          side: "right",
          text: resolved.userText,
          time: conversationCopy.now,
        });
      }

      if (resolvedFollowUps.length > 0) {
        nextItems.push(...resolvedFollowUps);
      }

      return nextItems;
    });

    setDraft("");
    setSelectedOptions([]);

    if (resolved.route) {
      navigate(resolved.route);
    }
  }

  async function handleDeleteForMe() {
    if (!deleteTarget?.messageId || !resolvedConversationId || !currentUserId) return;
    const deletedMessageId = deleteTarget.messageId;

    const nextHiddenMessageIds = Array.from(
      new Set([...hiddenMessageIds, deletedMessageId])
    );
    setDeleteBusyAction("mine");
    writeHiddenMessageIds(currentUserId, resolvedConversationId, nextHiddenMessageIds);
    setHiddenMessageIds(nextHiddenMessageIds);

    try {
      await loadLiveThread(
        resolvedConversationId,
        null,
        new Set(nextHiddenMessageIds)
      );
      setDeleteTarget(null);
      setToast({
        message: conversationCopy.toast.deletedForMe,
        actionLabel: conversationCopy.toast.undo,
        onAction: async () => {
          const restoredMessageIds = nextHiddenMessageIds.filter(
            (messageId) => messageId !== deletedMessageId
          );
          writeHiddenMessageIds(currentUserId, resolvedConversationId, restoredMessageIds);
          setHiddenMessageIds(restoredMessageIds);
          setToast({ message: "", actionLabel: "", onAction: null });
          await loadLiveThread(
            resolvedConversationId,
            null,
            new Set(restoredMessageIds)
          );
        },
      });
    } finally {
      setDeleteBusyAction("");
    }
  }

  async function handleDeleteForBoth() {
    if (!deleteTarget?.messageId || !resolvedConversationId) return;

    setDeleteBusyAction("both");

    try {
      await messagingService.deleteMessageForEveryone(deleteTarget.messageId);
      await loadLiveThread(resolvedConversationId);
      setDeleteTarget(null);
      setToast({
        message: conversationCopy.toast.deletedForBoth,
        actionLabel: "",
        onAction: null,
      });
    } catch (error) {
      setThreadError(error?.message || conversationCopy.toast.deleteError);
      setToast({
        message: error?.message || conversationCopy.toast.deleteError,
        actionLabel: "",
        onAction: null,
      });
    } finally {
      setDeleteBusyAction("");
    }
  }

  function handleFileChange(event) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || thinking || folaRevealing || loading) return;

    if (isFolaConversation) {
      const reader = new FileReader();
      reader.onload = async () => {
        const result = String(reader.result || "").trim();
        if (!result) return;

        setItems((current) => [
          ...current,
          {
            id: `image-${Date.now()}`,
            type: "image",
            side: "right",
            src: result,
            time: conversationCopy.now,
          },
          {
            id: `fola-image-reply-${Date.now() + 1}`,
            type: "text",
            side: "left",
            text: `${folaUiCopy.states.imageReceived} ${folaUiCopy.symptom.writeup}`,
            time: conversationCopy.now,
            outputType: "support",
            sourceContext: folaSourceContext,
          },
        ]);
      };
      reader.readAsDataURL(file);
      return;
    }

    if (!resolvedConversationId) return;

    const previewUrl = URL.createObjectURL(file);
    setPendingAttachment((current) => {
      releaseAttachmentPreview(current);
      return {
        file,
        fileName: String(file.name || "").trim(),
        previewUrl,
      };
    });
  }

  return (
    <div
      data-testid="message-conversation-layout"
      className="flex h-full min-h-0 w-full max-w-full flex-col overflow-hidden bg-[rgb(var(--clr-surface-cont-highest))]"
    >
      <ConversationTopBar
          title={resolvedTitle}
          onBack={() => {
            clearLastMessageThreadPath(currentUserId);
            navigate(returnTo || messagesBasePath, {
              ...(returnToState ? { state: returnToState } : {}),
              replace: true,
            });
          }}
          onOpenFola={openFolaForCurrentThread}
          onReset={openNewMessageComposer}
          backLabel={conversationCopy.topBar.back}
          openFolaLabel={conversationCopy.topBar.openFola}
          composeLabel={conversationCopy.topBar.compose}
        />

        <div className="flex min-h-0 flex-1 flex-col">
          <div
            ref={timelineRef}
            onScroll={handleTimelineScroll}
            tabIndex={0}
            aria-label="Conversation history"
            data-testid="message-history-timeline"
            className="min-h-0 flex-1 touch-pan-y overflow-x-hidden overflow-y-auto overscroll-contain pt-4 pb-4"
          >
            {!isFolaConversation &&
            currentRole === "doctor" &&
            resolvedConversationId &&
            !loading ? (
              <div className="mb-4 flex justify-end">
                <button
                  type="button"
                  onClick={openFolaForCurrentThread}
                  className="rounded-[12px] bg-[var(--state-primary-12)] px-4 py-2 text-[14px] font-medium leading-5 text-[rgb(var(--afrimed-primary))]"
                >
                  {t(
                    "messages.conversation.threadFolaCta",
                    "Use Fola with this thread"
                  )}
                </button>
              </div>
            ) : null}

            {threadError || threadRefreshError ? (
              <div role="alert" className="mb-4 rounded-[12px] bg-[rgba(180,35,24,0.08)] px-4 py-3 text-[14px] leading-5 text-[#b42318]">
                {threadError || threadRefreshError}
              </div>
            ) : null}

            {loadingOlderMessages ? (
              <div className="pb-3 text-center text-[13px] leading-[18px] text-[rgb(var(--clr-on-surface-variant))]">Loading earlier messages…</div>
            ) : null}

            {loading ? (
              <div className="flex h-[40vh] flex-col items-center justify-center gap-3 text-center text-[14px] leading-5 tracking-[-0.336px] text-[rgb(var(--clr-on-surface-variant))]">
                <IndeterminateProgressIndicator />
                <span>{t("messages.loadingConversation", "Loading conversation...")}</span>
              </div>
            ) : items.length > 0 ? (
              <div className="space-y-3">
                {items.map((item) => (
                  <MessageBubble
                    key={item.id}
                    item={item}
                    onOpenActions={(target) => setDeleteTarget(target)}
                    onFolaAction={handleFolaChoice}
                  />
                ))}
              </div>
            ) : (
              <div className="flex h-[40vh] items-center justify-center text-center text-[14px] leading-5 tracking-[-0.336px] text-[rgb(var(--clr-on-surface-variant))]">
                {t("messages.noMessagesYet", "No messages yet.")}
              </div>
            )}
          </div>

          <div className="pointer-events-none h-px w-full bg-[rgba(213,215,220,0.18)]" />

          <div
            data-testid="message-composer-region"
            className="relative shrink-0 bg-[rgb(var(--clr-surface-cont-highest))] pb-[calc(96px+env(safe-area-inset-bottom))] pt-2 md:pb-6"
          >
            <Composer
              isFolaConversation={isFolaConversation}
              thinking={thinking}
              draft={draft}
              pendingAttachment={pendingAttachment}
              disabled={loading || folaRevealing}
              actionMode={folaActionMode}
              placeholder={
                isFolaConversation
                  ? folaUiCopy.askAnything
                  : t("messages.writeMessage", "Write a message...")
              }
              thinkingPlaceholder={governedFolaStage || folaUiCopy.states.thinking}
              selectedOptions={selectedOptions}
              optionChoices={folaOptionChoices}
              onToggleOption={handleToggleOption}
              onDraftChange={(nextDraft) => {
                setDraft(nextDraft);
                setThreadError((current) => current === "message_required" ? "" : current);
              }}
              onAttach={handleAttachClick}
              onSend={handleSendText}
              onSelectPromptChoice={handleFolaChoice}
              draftStorageKey={currentDraftStorageKey}
              languageCode={preferredLanguageCode}
              allLabel={conversationCopy.composer.all}
              attachLabel={conversationCopy.composer.attach}
              sendLabel={conversationCopy.composer.send}
              thinkingLabel={conversationCopy.composer.thinking}
              selectedAttachmentAlt={conversationCopy.composer.selectedAttachmentAlt}
            />
          </div>
        </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
      />

      <MessageDeleteDialog
        open={Boolean(deleteTarget)}
        busyAction={deleteBusyAction}
        onClose={() => {
          if (deleteBusyAction) return;
          setDeleteTarget(null);
          setPendingDeleteTargetMessageId("");
        }}
        onDeleteForMe={handleDeleteForMe}
        onDeleteForBoth={handleDeleteForBoth}
        closeLabel={conversationCopy.deleteDialog.close}
        title={conversationCopy.deleteDialog.title}
        body={conversationCopy.deleteDialog.body}
        deletingLabel={conversationCopy.deleteDialog.deleting}
        deleteForMeLabel={conversationCopy.deleteDialog.deleteForMe}
        deleteForBothLabel={conversationCopy.deleteDialog.deleteForBoth}
      />

      <MessageToast
        toast={toast}
        dismissLabel={conversationCopy.toast.dismiss}
        onClose={() => setToast({ message: "", actionLabel: "", onAction: null })}
      />
    </div>
  );
}
